import { toFixed } from "@shared/utils/numbers";
import {
  AdvancedSettings,
  AmountType,
  AnalyticsPageName,
  BaseBotData,
  BaseBotGeneralData,
  BaseStrategyData,
  BotConfigurations,
  BotData,
  BotOptions,
  BotType,
  BuiltInStrategyData,
  ClientData,
  ConfigurationItem,
  CreateBotRequest,
  CustomStrategyData,
  DCAStrategyData,
  DefaultOrder,
  ExchangeName,
  LanguageNameSpaces,
  LongShort,
  MarginTypes,
  NumberOption,
  OpenMessage,
  OrderBaseline,
  OverrideData,
  OverrideFieldType,
  SortingOrder,
  StopType,
  StrategyData,
  StrategyStyleType,
  StrategyType,
  TriggerType,
} from "@shared/types";
import {
  ceil,
  difference,
  isNumber,
  isObject,
  mapValues,
  minBy,
  partition,
  xor,
} from "lodash";
import { OrderTypes } from "@shared/features/trades/common/types";
import { BotAmountData } from "@shared/features/bots/types";
import {
  getNormalizedPercentages,
  getOrdersDefaultSortOrder,
} from "@shared/features/trades/common/utils";
import {
  BaseBotFormValues,
  BotFormSetters,
  BotFormType,
  BotFormValues,
  ChannelValue,
  Empty,
  Error,
  GridsData,
  GridType,
  HedgingMode,
  InitialBotAmountData,
  MultiToggleValue,
  MultiValue,
  OverlapData,
  ParsedDcaStrategyData,
  PercentageBaseType,
  SharedOrderData,
  SharedStrategyOrder,
  StrategyBaseline,
  StrategyOrder,
  StrategyOrdersOptions,
  StrategyTabs,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import {
  ADVANCED_BOT_FORM_KEYS,
  ADVANCED_SETTINGS_KEYS,
  ALL_OVERRIDES_TOGGLE_FIELDS,
  BOT_FORM_KEY_TO_SETTER_NAME,
  channelValue,
  DEFAULT_FIRST_ENTRY_TAKE_PROFIT,
  DEFAULT_GRID_BOT_STOP,
  DEFAULT_GRID_BOT_STRATEGY,
  DEFAULT_NUMBER_OF_SIMULTANEOUS_TRADES_PER_SYMBOL,
  DEFAULT_OVERRIDE_FIELDS,
  DEFAULT_SIGNAL_BOT_TAKE_PROFIT,
  DEFAULT_STOP,
  DEFAULT_STOP_LIMIT_PRICE_REDUCTION,
  DEFAULT_TAKE_PROFIT,
  empty,
  ENTRY_STRATEGY_TOGGLE_FIELDS,
  FIELD_TO_PARENT_FIELD,
  GRID_MAX_PRICE_DIFFERENCE_TEXT,
  MAX_DEFAULT_TAKE_PROFIT_PERCENT,
  MAX_NUM_GRIDS,
  MAX_PRICE,
  MAX_PRICE_DIFFERENCE_TEXT,
  MIN_CONSECUTIVE_GRIDS_DISTANCE,
  MIN_NUM_GRIDS,
  multiToggleValue,
  off,
  OVERLAP_KEY_TO_DISPLAY,
  STRATEGY_TYPE_MAPPING,
  TOGGLE_FIELD_TO_FORM_KEY,
  WITHOUT_OFF_TOGGLE_FIELDS,
} from "@shared/features/bots/BotForm/common/constants";
import {
  getCreateItem,
  getItemByName,
  getItemValidations,
  getItemValue,
} from "@shared/features/configuration/utils";
import { sortByNumberProperty } from "@shared/utils/general";
import {
  BINANCE_EXCHANGES,
  LONG_SHORT_TO_DISPLAY,
  MARGIN_TYPE_TO_DISPLAY,
} from "@shared/common/constants";
import {
  getBotMultiSymbolMarginData,
  getBotParsedTrailingStop,
  getConfigurationItem,
  getConfigurationItemValue,
  getDefaultOrder,
  getIsPercentAmountType,
  getOptionsItemValue,
  getOverrideValue,
  getStrategy,
} from "@shared/features/bots/utils";
import { DCA_STRATEGIES_ITEM_NAME } from "@shared/features/configuration/constants";
import { isSymbol } from "@shared/types/utils";
import {
  MIN_BINANCE_USD_AMOUNT_PER_TRADE,
  MIN_USD_AMOUNT_PER_TRADE,
} from "@shared/features/onboarding/constants";
import { findLastNonZeroValue } from "@shared/utils/arrays";
import { sortByExchangeOrder } from "@shared/utils/misc";
import { TranslationKeys } from "@shared/common/i18n/types";
import { translateOptionName } from "@shared/common/i18n/optionNames";

export const getOrderScaleDifference = (
  difference: number | undefined,
  scale: number | undefined,
  orderNumber: number
) => {
  if (difference && scale) {
    return difference * Math.pow(scale, orderNumber);
  }
  return 0;
};

const getOrdersRelativeDifferences = (
  numOrders: number | undefined,
  difference: number | undefined,
  scale: number | undefined
) => {
  return [...Array(numOrders || 0).keys()].map((orderNumber) => {
    return getOrderScaleDifference(difference, scale, orderNumber);
  });
};

const getOrdersAbsoluteDifferences = (
  relativeDifferences: number[],
  isIncreasing?: boolean
) => {
  return relativeDifferences.map((_, index) => {
    const sum = relativeDifferences
      .slice(0, index + 1)
      .reduce((acc, relativeDifference) => {
        const ratio = relativeDifference / 100;
        const delta = isIncreasing ? 1 + ratio : 1 - ratio;
        return acc * delta;
      }, 1);
    return sum * 100;
  });
};

export const getOrdersAbsoluteDifferencesList = (
  numOrders: number | undefined,
  priceDifference: number | undefined,
  priceScale: number | undefined,
  isIncreasing: boolean
) => {
  if (numOrders) {
    const priceDifferences = getOrdersRelativeDifferences(
      numOrders - 1,
      priceDifference,
      priceScale
    );
    return getOrdersAbsoluteDifferences(priceDifferences, isIncreasing);
  }
  return [];
};

export const getFirstOrderBaselineDifferencesList = (
  numOrders: number | undefined,
  priceDifference: number | undefined,
  priceScale: number | undefined,
  isIncreasing: boolean
) => {
  if (numOrders) {
    const priceDifferences = getOrdersRelativeDifferences(
      numOrders - 1,
      priceDifference,
      priceScale
    );
    return priceDifferences.map((_, index) => {
      const sum = priceDifferences
        .slice(0, index + 1)
        .reduce((acc, relativeDifference) => {
          return acc + relativeDifference;
        }, 0);
      return isIncreasing ? 100 + sum : 100 - sum;
    });
  }
  return [];
};

export const getOrdersPrices = (
  numOrders: ParsedDcaStrategyData["numOrders"],
  priceDifference: ParsedDcaStrategyData["priceDifference"],
  priceScale: ParsedDcaStrategyData["priceScale"],
  baseline: ParsedDcaStrategyData["baseline"],
  basePrice: number,
  pricePrecision: number,
  orderType: OrderTypes,
  reverse: boolean
) => {
  if (isSymbol(priceDifference) || isSymbol(priceScale) || isSymbol(numOrders))
    return [];
  const sortOrder = getOrdersDefaultSortOrder(orderType, reverse, false);
  const isIncreasing = sortOrder === SortingOrder.ASCEND;
  let overflow = false;
  let ordersDifferences: number[] = [];
  if (numOrders) {
    ordersDifferences =
      baseline === StrategyBaseline.FIRST_ORDER
        ? getFirstOrderBaselineDifferencesList(
            numOrders,
            priceDifference,
            priceScale,
            isIncreasing
          )
        : getOrdersAbsoluteDifferencesList(
            numOrders,
            priceDifference,
            priceScale,
            isIncreasing
          );
  }
  const ordersPrices = ordersDifferences.map((orderDifference) => {
    const ratio = orderDifference / 100;
    const price = toFixed(ratio * basePrice, pricePrecision);
    if (isIncreasing && (overflow || price > MAX_PRICE)) {
      overflow = true;
      return MAX_PRICE;
    } else if (!isIncreasing && (overflow || price <= 0)) {
      overflow = true;
      return 0;
    }
    return price;
  });

  return [basePrice, ...ordersPrices];
};

export const getDCAStrategyRatios = (
  numOrders: number | undefined,
  amountScale: number | undefined,
  firstOrderPercentage?: number | null
) => {
  const hasFirstOrderPercentage = typeof firstOrderPercentage === "number";
  const amountDifferences = getOrdersRelativeDifferences(
    numOrders,
    1,
    amountScale
  );
  let ratios = getNormalizedPercentages(
    hasFirstOrderPercentage
      ? amountDifferences.slice(0, amountDifferences.length - 1)
      : amountDifferences,
    2,
    hasFirstOrderPercentage ? 100 - firstOrderPercentage : undefined,
    false
  );
  if (hasFirstOrderPercentage) {
    ratios = [firstOrderPercentage, ...ratios];
  }
  return ratios;
};

export const getParsedMultiSymbolMarginData = (
  multiSymbolMarginData: BaseBotFormValues["multiSymbolMarginData"]
) => {
  if (isSymbol(multiSymbolMarginData) || !multiSymbolMarginData)
    return multiSymbolMarginData;
  const getParsedValue = (value: any) => (isSymbol(value) ? undefined : value);
  return mapValues(multiSymbolMarginData, (value) => [
    getParsedValue(value.marginType),
    getParsedValue(value.multiplier),
    getParsedValue(value.leverageType),
  ]);
};

export const getParsedStrategy = (
  botType: BotType,
  strategy: BaseBotFormValues["entryStrategy"],
  isChannelConfiguration?: boolean
) => {
  const isSignalsBot = botType === BotType.SIGNAL;
  if (isSymbol(strategy)) return strategy;
  else if (isSymbol(strategy?.strategy_style) && !isSignalsBot)
    return strategy?.strategy_style;
  if (strategy) {
    const parsedStrategy: StrategyData = {
      ...strategy,
      // @ts-ignore
      strategy_style:
        strategy.strategy_style === StrategyStyleType.BUILT_IN &&
        isChannelConfiguration
          ? StrategyStyleType.LEGACY_BUILT_IN
          : strategy.strategy_style,
      price_difference:
        !strategy.price_difference || isSymbol(strategy.price_difference)
          ? strategy.price_difference
          : (percentToRatio(strategy.price_difference, false) as MultiValue),
    };
    if (isCustomStrategy(parsedStrategy)) {
      parsedStrategy.targets = isSymbol(parsedStrategy.targets)
        ? parsedStrategy.targets
        : parsedStrategy.targets?.map(
            (target) => percentToRatio(target) as number
          );
    } else if ("first_order_percentage" in parsedStrategy) {
      parsedStrategy.first_order_percentage = isSymbol(
        parsedStrategy.first_order_percentage
      )
        ? parsedStrategy.first_order_percentage
        : percentToRatio(parsedStrategy.first_order_percentage);
    }
    return mapValues(parsedStrategy, (value) => {
      if (isSignalsBot && (isSymbol(value) || !value)) {
        return null;
      } else if (isSymbol(value)) {
        return empty;
      }
      return value;
    });
  }
};

export const percentToRatio = (
  percent: BaseBotFormValues["trailingEntry"] | string,
  applyPrecision = true
) => {
  if (percent === undefined || isSymbol(percent)) return percent;
  let parsedPercent: number;
  if (typeof percent === "string") {
    parsedPercent = parseFloat(percent);
  } else {
    parsedPercent = percent;
  }
  parsedPercent = parsedPercent / 100;
  if (applyPrecision) parsedPercent = toFixed(parsedPercent, 4);
  return parsedPercent;
};

export const getParsedDefaultOrder = (
  defaultOrder?:
    | BaseBotFormValues["defaultTakeProfit"]
    | BaseBotFormValues["defaultStop"]
) => {
  if (!defaultOrder || isSymbol(defaultOrder)) return defaultOrder;
  return {
    baseline: convertEmptyValue(defaultOrder.baseline),
    percent: convertEmptyValue(percentToRatio(defaultOrder.percent)),
  };
};

export const getParsedTrailingStop = (
  trailingStopData?: BaseBotFormValues["trailingStopData"]
) => {
  if (!trailingStopData || isSymbol(trailingStopData)) return trailingStopData;
  if (trailingStopData.type === 0) return off;
  const value = trailingStopData.value;
  const trigger = trailingStopData.trigger;
  const triggerValue = trigger?.value;
  const triggerType =
    trailingStopData.type === 1 || trailingStopData.type === 5
      ? TriggerType.TARGET
      : trigger?.type;
  return {
    type: trailingStopData.type,
    value: value ? percentToRatio(value) : value,
    trigger: {
      type: triggerType,
      value:
        triggerType === TriggerType.PERCENT && triggerValue
          ? percentToRatio(triggerValue)
          : triggerValue,
    },
  };
};

export const getStrategyNumOrders = (
  strategy: BaseBotFormValues["entryStrategy"]
) => {
  if (isSymbol(strategy) || !strategy) return 0;
  if (isCustomStrategy(strategy)) {
    return isSymbol(strategy.targets) || !strategy.targets
      ? 0
      : strategy.targets.length;
  } else if ("number_of_orders" in strategy) {
    return isSymbol(strategy.number_of_orders) ? 0 : strategy.number_of_orders;
  }
  return 0;
};

export const getStrategyRatios = (strategy: StrategyData, isGrids: boolean) => {
  let ratios: (number | string)[] = [];
  if (!isSymbol(strategy)) {
    if (isDcaStrategy(strategy)) {
      const { amount_scale, number_of_orders, first_order_percentage } =
        strategy;
      ratios =
        !isSymbol(number_of_orders) &&
        !isSymbol(amount_scale) &&
        !isSymbol(first_order_percentage)
          ? getDCAStrategyRatios(
              number_of_orders,
              amount_scale,
              isGrids ? 0 : first_order_percentage
            )
          : [];
    } else if ("targets" in strategy && !isSymbol(strategy.targets)) {
      ratios = strategy.targets || [];
    }
  }
  return ratios.map((percentage) => Number(percentage) / 100);
};

const getGridPendingOrders = (
  currentPrice: number,
  firstOrderPrice: number,
  lastOrderPrice: number,
  isReverse: boolean,
  isGridBot: boolean
) => {
  if (!isGridBot) return [false, false];
  let firstOrderIsPending;
  let lastOrderIsPending;
  if (isReverse) {
    firstOrderIsPending = currentPrice < firstOrderPrice;
    lastOrderIsPending = currentPrice > lastOrderPrice;
  } else {
    firstOrderIsPending = currentPrice > firstOrderPrice;
    lastOrderIsPending = currentPrice < lastOrderPrice;
  }
  return [firstOrderIsPending, lastOrderIsPending];
};

const getHedgingGridOrdersRatios = (
  hedgingGridsData: BaseBotFormValues["hedgingGridsData"],
  strategy: BaseBotFormValues["entryStrategy"]
) => {
  if (!validateGridsData(hedgingGridsData).isValid) {
    return [];
  }
  return Array.from({ length: getStrategyNumOrders(strategy) || 0 }, () => 1);
};

const getHedgingBotOrderRatios = (
  longHedgingRatio?: BaseBotFormValues["longHedgingRatio"]
) => {
  const parsedLongHedgingRatio =
    !isSymbol(longHedgingRatio) && longHedgingRatio !== undefined
      ? longHedgingRatio
      : 50;
  const longRatio = parsedLongHedgingRatio / 100;
  return [longRatio, 1 - longRatio].filter((ratio) => ratio > 0);
};

const getParsedLongHedgingRatio = (
  longHedgingRatio?: BaseBotFormValues["longHedgingRatio"]
) =>
  !isSymbol(longHedgingRatio) && longHedgingRatio !== undefined
    ? longHedgingRatio
    : 50;

const getHedgingSplitOrderIndexes = (longHedgingRatio: number) => {
  const longOrderIndex = longHedgingRatio > 0 ? 0 : -1;
  const shortOrderIndex =
    longHedgingRatio < 100 ? (longHedgingRatio > 0 ? 1 : 0) : -1;
  return { longOrderIndex, shortOrderIndex };
};

const getHedgingSplitOrders = (
  botAmountData: BotAmountData | undefined,
  price: number,
  getMinAmountByPrice: ((price: number) => number) | undefined,
  longHedgingRatio: number | undefined
) => {
  const parsedLongRatio = longHedgingRatio ?? 50;
  const ratios = getHedgingBotOrderRatios(parsedLongRatio);
  const orders = getStrategyOrders(
    botAmountData,
    price,
    0,
    false,
    OrderTypes.ENTRY,
    undefined,
    getMinAmountByPrice,
    undefined,
    { ordersRatiosOverride: ratios }
  );
  const { longOrderIndex, shortOrderIndex } =
    getHedgingSplitOrderIndexes(parsedLongRatio);
  return {
    longOrder: longOrderIndex >= 0 ? orders[longOrderIndex] : undefined,
    shortOrder: shortOrderIndex >= 0 ? orders[shortOrderIndex] : undefined,
  };
};

const hasValue = (value: unknown) => value !== null && value !== undefined;

export const getStrategyOrders = (
  botAmountData: BotAmountData | undefined,
  basePrice: number,
  pricePrecision: number,
  reverse: boolean,
  orderType: OrderTypes,
  strategy?: BaseBotFormValues["entryStrategy"],
  getMinAmountByPrice?: (price: number) => number,
  gridsData?: BaseBotFormValues["gridsData"],
  options?: StrategyOrdersOptions
): StrategyOrder[] => {
  const isHedgingOrders =
    !!options &&
    ("longHedgingRatio" in options || "hedgingGridsData" in options);
  const hedgingGridOrdersRatios = options?.hedgingGridsData
    ? getHedgingGridOrdersRatios(options.hedgingGridsData, strategy)
    : undefined;
  const gridsDataToUse = options?.hedgingGridsData ?? gridsData;
  const customOrderRatios =
    options?.ordersRatiosOverride ?? hedgingGridOrdersRatios;
  const ordersRatios =
    customOrderRatios ??
    (strategy && !isSymbol(strategy)
      ? getStrategyRatios(strategy, !!gridsDataToUse)
      : [1]);
  const baseStrategyPrice = gridsDataToUse
    ? getGridsParsedBasePrice(gridsDataToUse, reverse) || 0
    : basePrice;
  const numActiveOrders =
    !!strategy &&
    !isSymbol(strategy) &&
    !isSymbol(strategy.number_of_active_orders) &&
    strategy.number_of_active_orders;
  let ordersPrices = [basePrice];
  if (strategy && !isSymbol(strategy)) {
    ordersPrices = getOrdersPrices(
      getStrategyNumOrders(strategy),
      strategy.price_difference,
      strategy.price_scale,
      strategy.baseline,
      baseStrategyPrice,
      pricePrecision,
      orderType,
      reverse
    );
  }
  const mainCoinAmount = botAmountData?.mainCoinAmount || 0;
  let amountSum = 0;
  const firstOrderPrice = ordersPrices[0];
  const lastOrderPrice = ordersPrices[ordersPrices.length - 1];
  const [firstOrderIsPending, lastOrderIsPending] = getGridPendingOrders(
    basePrice,
    firstOrderPrice,
    lastOrderPrice,
    reverse,
    !!gridsDataToUse
  );
  const isEntryOrders = orderType === OrderTypes.ENTRY;
  let numProcessedActiveOrders = 0;
  return ordersRatios.map((orderRatio, index) => {
    const orderAmount = orderRatio * mainCoinAmount;
    amountSum += toFixed(
      orderAmount,
      botAmountData?.mainCoinAmountPrecision || 0
    );
    const orderPrice =
      (customOrderRatios
        ? ordersPrices[index] ?? ordersPrices[0]
        : ordersPrices[index]) || 0;
    const minAmount = getMinAmountByPrice?.(orderPrice) || 0;
    let isValid = minAmount ? orderAmount >= minAmount : true;
    if (gridsDataToUse && index === 0) isValid = true;
    const longHedgingRatio = options?.hedgingGridsData
      ? getHedgingLongRatioByPrice(orderPrice, options.hedgingGridsData)
      : isHedgingOrders
      ? getParsedLongHedgingRatio(options?.longHedgingRatio)
      : undefined;
    const shortHedgingRatio =
      longHedgingRatio !== undefined ? toFixed(100 - longHedgingRatio, 2) : 50;
    const hedgingSplitRatio =
      longHedgingRatio ?? (isHedgingOrders ? 50 : undefined);
    const { longOrder, shortOrder } =
      hedgingSplitRatio !== undefined
        ? getHedgingSplitOrders(
            botAmountData,
            orderPrice,
            getMinAmountByPrice,
            hedgingSplitRatio
          )
        : { longOrder: undefined, shortOrder: undefined };
    if (isHedgingOrders) {
      isValid =
        (!longOrder || longOrder.isValid) &&
        (!shortOrder || shortOrder.isValid);
    }
    const isImmediate =
      isEntryOrders &&
      ((reverse && orderPrice <= basePrice) ||
        (!reverse && orderPrice >= basePrice));
    const isActive =
      isEntryOrders &&
      (isImmediate ||
        (!!numActiveOrders && numProcessedActiveOrders < numActiveOrders));
    if (isActive && !isImmediate) numProcessedActiveOrders += 1;
    return {
      amount: orderAmount,
      ratio: orderRatio,
      price: orderPrice,
      minAmount: isHedgingOrders
        ? Math.max(longOrder?.minAmount || 0, shortOrder?.minAmount || 0)
        : minAmount,
      usdAmount: botAmountData?.convertMainCoinAmountToUSD(orderAmount) || 0,
      btcAmount: botAmountData?.convertMainCoinAmountToBTC(orderAmount) || 0,
      isValid,
      availableAmountWarning:
        !!botAmountData && amountSum > botAmountData.mainCoinAvailableAmount,
      isPending:
        (firstOrderIsPending && index === 0) ||
        (lastOrderIsPending && index === ordersRatios.length - 1),
      isImmediate,
      isActive,
      longHedgingRatio,
      shortHedgingRatio: isHedgingOrders ? shortHedgingRatio : undefined,
      longHedgingOrder: longOrder,
      shortHedgingOrder: shortOrder,
    };
  });
};

const getParsedPercent = (value?: string | number | MultiValue) => {
  if (isSymbol(value) || value === undefined || value === "") return;
  const parsedValue = typeof value === "number" ? value : parseFloat(value);
  return isNaN(parsedValue) ? undefined : parsedValue;
};

export const isHedgingRangeMode = (formValues: BaseBotFormValues) =>
  formValues.hedgingMode === HedgingMode.RANGE;

export const isHedgingRangeStrategyMixed = (
  formValues: Pick<
    BaseBotFormValues,
    "hedgingMode" | "entryStrategy" | "hedgingGridsData"
  >
) =>
  formValues.hedgingMode === HedgingMode.RANGE &&
  (formValues.entryStrategy === multiToggleValue ||
    isSymbol(formValues.hedgingGridsData?.gridType) ||
    isSymbol(formValues.hedgingGridsData?.numGrids) ||
    isSymbol(formValues.hedgingGridsData?.priceRange?.min) ||
    isSymbol(formValues.hedgingGridsData?.priceRange?.max));

export const getHedgingLongRatioByPrice = (
  price: number,
  hedgingGridsData: BaseBotFormValues["hedgingGridsData"]
) => {
  const minPrice = getParsedPercent(hedgingGridsData?.priceRange?.min);
  const maxPrice = getParsedPercent(hedgingGridsData?.priceRange?.max);
  const lowerRatio = getParsedPercent(
    hedgingGridsData?.lowerPriceLongExposureRatio
  );
  const upperRatio = getParsedPercent(
    hedgingGridsData?.upperPriceLongExposureRatio
  );
  if (
    minPrice === undefined ||
    maxPrice === undefined ||
    lowerRatio === undefined ||
    upperRatio === undefined ||
    minPrice === maxPrice
  ) {
    return;
  }
  const distanceRatio = (price - minPrice) / (maxPrice - minPrice);
  return toFixed(lowerRatio + (upperRatio - lowerRatio) * distanceRatio, 2);
};

export const getHedgingValidationOrders = (orders?: StrategyOrder[]) =>
  orders?.flatMap(
    (order) =>
      [order.longHedgingOrder, order.shortHedgingOrder].filter(
        Boolean
      ) as StrategyOrder[]
  ) || [];

export const validateHedgingGridsData = (
  hedgingGridsData: BaseBotFormValues["hedgingGridsData"]
) => {
  const gridsValidation = validateGridsData(hedgingGridsData);
  const lowerRatio = getParsedPercent(
    hedgingGridsData?.lowerPriceLongExposureRatio
  );
  const upperRatio = getParsedPercent(
    hedgingGridsData?.upperPriceLongExposureRatio
  );
  const lowerRatioIsMulti = isSymbol(
    hedgingGridsData?.lowerPriceLongExposureRatio
  );
  const upperRatioIsMulti = isSymbol(
    hedgingGridsData?.upperPriceLongExposureRatio
  );
  const exposureErrorMessage =
    (!lowerRatioIsMulti && lowerRatio === undefined) ||
    (!upperRatioIsMulti && upperRatio === undefined)
      ? "Please provide long hedging values for both range prices."
      : (lowerRatio !== undefined && (lowerRatio < 0 || lowerRatio > 100)) ||
        (upperRatio !== undefined && (upperRatio < 0 || upperRatio > 100))
      ? "The hedging values should be between 0% and 100%"
      : undefined;
  return {
    ...gridsValidation,
    exposureErrorMessage,
    isValid: gridsValidation.isValid && !exposureErrorMessage,
  };
};

export const getClosestHedgingGridOrder = (
  currentPrice: number | undefined,
  orders: StrategyOrder[] | undefined
) => {
  if (!currentPrice || !orders?.length) return;
  return minBy(orders, (order) => Math.abs(order.price - currentPrice));
};

export interface HedgingExposureBreakdown {
  longRatio: number;
  shortRatio: number;
  longAmountUsd: number;
  shortAmountUsd: number;
}

/**
 * Resolves the long/short hedging split (ratios + USD amounts) for a hedging
 * bot. In range mode the split is taken from the grid order closest to the
 * current price; otherwise it is derived from the fixed long hedging ratio.
 * Returns undefined when the split can't be determined (range mode without a
 * matching grid order, or a mixed/undefined ratio in fixed mode).
 */
export const getHedgingExposureBreakdown = (
  formValues: BaseBotFormValues,
  longHedgingRatio: BaseBotFormValues["longHedgingRatio"],
  botAmountData: BotAmountData | undefined,
  parsedEntries: StrategyOrder[] | undefined,
  currentPrice: number | undefined
): HedgingExposureBreakdown | undefined => {
  const closestGridOrder = getClosestHedgingGridOrder(
    currentPrice,
    parsedEntries
  );
  const longRatio = isHedgingRangeMode(formValues)
    ? closestGridOrder?.longHedgingRatio
    : isSymbol(longHedgingRatio)
    ? undefined
    : longHedgingRatio;
  if (longRatio === undefined) return undefined;
  const shortRatio = toFixed(100 - longRatio, 2);
  const mainCoinAmount = botAmountData?.mainCoinAmount || 0;
  const longAmountUsd =
    closestGridOrder?.longHedgingOrder?.usdAmount ??
    botAmountData?.convertMainCoinAmountToUSD(
      (longRatio / 100) * mainCoinAmount
    ) ??
    0;
  const shortAmountUsd =
    closestGridOrder?.shortHedgingOrder?.usdAmount ??
    botAmountData?.convertMainCoinAmountToUSD(
      (shortRatio / 100) * mainCoinAmount
    ) ??
    0;
  return { longRatio, shortRatio, longAmountUsd, shortAmountUsd };
};

export const getBotMainCoinMinAmount = (
  parsedEntries: SharedStrategyOrder[],
  validateOrderConvertedAmount: (amount: number, price: number) => boolean
) => {
  let minBotAmount: number | undefined;
  let maxMinAmount = 0;
  parsedEntries.forEach((entry, index) => {
    const currentEntryMinBotAmount =
      (entry.ratio ? 1 / entry.ratio : 0) * (entry.minAmount || 0);
    if (currentEntryMinBotAmount > maxMinAmount)
      maxMinAmount = currentEntryMinBotAmount;
    let ordersValid = true;
    for (let [innerIndex, innerEntry] of parsedEntries.entries()) {
      if (innerIndex !== index) {
        const innerEntryAmount = currentEntryMinBotAmount * innerEntry.ratio;
        if (
          innerEntryAmount < (innerEntry.minAmount || 0) ||
          !validateOrderConvertedAmount?.(innerEntryAmount, innerEntry.price)
        ) {
          ordersValid = false;
          break;
        }
      }
    }
    if (
      ordersValid &&
      (!minBotAmount || currentEntryMinBotAmount < minBotAmount)
    ) {
      minBotAmount = currentEntryMinBotAmount;
    }
  });
  return (minBotAmount ?? maxMinAmount) || 0;
};

export const getBotMinAmount = (
  parsedValidationEntries: SharedStrategyOrder[] | undefined,
  botAmountData: BotAmountData | undefined,
  amountType: AmountType | undefined,
  amountPrecision: number,
  exchange: string | undefined,
  validateOrderConvertedAmount: (amount: number, price: number) => boolean,
  botType?: BotType,
  useSymbolFromAlert?: boolean | MultiValue
) => {
  const isSignalsBot = botType === BotType.SIGNAL;
  if (!isSignalsBot && !parsedValidationEntries) return;
  if (getIsPercentAmountType(amountType)) return 0;
  let minMainCoinBotAmount = 0;
  if (isSignalsBot || useSymbolFromAlert) {
    minMainCoinBotAmount = getMinUsdAmountPerTrade(exchange);
  } else if (parsedValidationEntries) {
    minMainCoinBotAmount = getBotMainCoinMinAmount(
      parsedValidationEntries,
      validateOrderConvertedAmount
    );
  }
  let minAmount =
    botAmountData?.convertMainCoinAmountToAmountType(
      minMainCoinBotAmount,
      false,
      amountType
    ) ?? minMainCoinBotAmount;
  let factor = 1;
  if (!isSignalsBot && !useSymbolFromAlert) {
    factor = 1.05;
    if (exchange === "KuCoin") {
      factor = 1.2;
    }
  }

  if (minAmount === undefined || isNaN(minAmount)) return;
  else if (isNumber(minAmount)) {
    return ceil(minAmount * factor, amountPrecision);
  }
  return minAmount;
};

export const getMaxBotAmount = (
  parsedEntries: StrategyOrder[] | undefined,
  botAmountData: BotAmountData,
  useSymbolFromAlert?: boolean | MultiValue,
  botType?: BotType
) => {
  if (getIsPercentAmountType(botAmountData.amountType)) {
    return 100;
  }
  if (!parsedEntries || useSymbolFromAlert) return;
  const shouldValidateAllEntries = botType === BotType.GRID;
  const immediateEntriesRatio = parsedEntries
    .filter(
      (order) => shouldValidateAllEntries || order.isImmediate || order.isActive
    )
    .reduce((sum, order) => sum + order.ratio, 0);
  if (immediateEntriesRatio) {
    const minMainCoinBotAmount =
      (1 / immediateEntriesRatio) * botAmountData.mainCoinAvailableAmount;
    return botAmountData.convertMainCoinAmountToAmountType(
      minMainCoinBotAmount,
      true
    );
  }
};

export const getDefaultOrderPrice = (
  defaultOrder: BaseBotFormValues["defaultStop"],
  parsedEntries: StrategyOrder[] | undefined,
  reverse: boolean,
  orderType: OrderTypes.STOP | OrderTypes.TAKE_PROFIT,
  botType?: BotType,
  argsBaselinePrice?: number
) => {
  const isValid = defaultOrder && !isSymbol(defaultOrder);
  const defaultRatioDifference =
    isValid && !isSymbol(defaultOrder.percent) && defaultOrder.percent
      ? parseFloat(defaultOrder.percent) / 100
      : 0;
  let ratioMultiplier = 1;
  if (
    (orderType === OrderTypes.STOP && !reverse) ||
    (orderType === OrderTypes.TAKE_PROFIT && reverse)
  ) {
    ratioMultiplier = -1;
  }
  const multiplier = 1 + ratioMultiplier * defaultRatioDifference;

  let baselinePrice = argsBaselinePrice ?? 0;
  if (
    !baselinePrice &&
    isValid &&
    defaultOrder.baseline &&
    !isSymbol(defaultOrder.baseline)
  ) {
    baselinePrice = getDefaultOrderBasePrice(
      defaultOrder.baseline,
      parsedEntries,
      botType
    );
  }
  return baselinePrice * multiplier;
};

export const getDefaultOrderBasePrice = (
  baseline: OrderBaseline | undefined,
  entries: SharedOrderData[] | undefined,
  botType?: BotType
) => {
  if (!entries?.length) return 0;
  const lastEntry = entries[entries.length - 1];
  const firstEntry = entries[0];

  if (baseline === OrderBaseline.FIRST_ENTRY) {
    return firstEntry.price;
  } else if (baseline === OrderBaseline.AVERAGE_ENTRIES) {
    const validationEntries =
      botType === BotType.GRID ? entries.slice(1) : entries;
    return validationEntries.reduce((weightedAverage, order) => {
      weightedAverage += order.price * order.ratio;
      return weightedAverage;
    }, 0);
  } else if (baseline === OrderBaseline.LAST_ENTRY) {
    return lastEntry.price;
  }
  return 0;
};

export const getMinStopPercent = (
  baseline: OrderBaseline | undefined,
  orders: SharedOrderData[],
  botType?: BotType
) => {
  let minStopRatio = 0;
  const lastEntry = orders[orders.length - 1];
  const basePrice = getDefaultOrderBasePrice(baseline, orders, botType);
  if (lastEntry && basePrice) {
    minStopRatio = (basePrice - lastEntry.price) / basePrice;
  }
  return Math.abs(toFixed(minStopRatio * 100, 2));
};

export const getMaxStopPercent = (
  isMarginExchange?: boolean,
  botType?: BotType,
  marginType?: MarginTypes | MultiValue
) => {
  if (
    isMarginExchange &&
    (botType === BotType.SIGNAL || marginType === MarginTypes.CROSS)
  )
    return undefined;
  return 90;
};

const validateStrategyValue = (
  value: number | undefined | MultiValue | MultiToggleValue | ChannelValue,
  max?: number,
  isOptional = false
) => {
  if (isSymbol(value)) return true;
  if (value === undefined) return isOptional;
  return (!max || value <= max) && value > 0;
};

export const getPercentChange = (firstPrice: number, secondPrice: number) => {
  let change = (secondPrice - firstPrice) / firstPrice;
  return change * 100;
};

export const getPriceRangeDistancePercent = (
  priceRange: GridsData["priceRange"] | undefined
) => {
  const { min, max } = priceRange || {};
  if (!min || !max || isSymbol(min) || isSymbol(max)) return 0;
  const minPrice = parseFloat(min);
  const maxPrice = parseFloat(max);
  return Math.abs(toFixed(getPercentChange(maxPrice, minPrice), 2));
};

const isPriceDifferenceInvalid = (
  first: number,
  second: number,
  isIncreasing: boolean
) => {
  if (first === undefined || second === undefined) return false;
  const percentChange = getPercentChange(first, second);
  return isIncreasing ? percentChange >= 400 : percentChange <= -80;
};

export const validateFirstOrderPercent = (
  includeFirstOrderPercent: boolean,
  ignoreFirstOrderPercent: boolean,
  firstOrderPercent: ParsedDcaStrategyData["firstOrderPercent"],
  maxFirstOrderPercentage: number
) => {
  if (
    !includeFirstOrderPercent ||
    ignoreFirstOrderPercent ||
    isSymbol(firstOrderPercent)
  )
    return;
  const maxFirstOrderPercent = maxFirstOrderPercentage * 100;
  if (firstOrderPercent) {
    if (firstOrderPercent === 0)
      return "Value should be greater than 0 or left unfilled";
    else if (firstOrderPercent > maxFirstOrderPercent)
      return `Value should not exceed ${maxFirstOrderPercent}%`;
  }
};

type StrategyPricesValidation = {
  maxPriceDifference?: number;
  maxPriceDifferenceError?: string;
  invalidPrices?: boolean;
  hasConsecutiveGridsDistanceError?: boolean;
};

const getEffectiveStrategyBaseline = (
  baseline: Partial<ParsedDcaStrategyData>["baseline"],
  gridsData: GridsData | undefined,
  botType?: BotType
) => {
  if (baseline !== undefined) return baseline;
  if (botType === BotType.GRID) {
    return gridsData?.gridType === GridType.ARITHMETIC
      ? StrategyBaseline.FIRST_ORDER
      : StrategyBaseline.PREVIOUS_ORDER;
  }
  return StrategyBaseline.PREVIOUS_ORDER;
};

export const validateStrategyPrices = (
  orderType: OrderTypes,
  strategy: Partial<ParsedDcaStrategyData>,
  longShort: MultiValue | LongShort[] | undefined,
  currentPrice: number,
  pricePrecision: number,
  gridsData: GridsData | undefined,
  botType?: BotType
): StrategyPricesValidation => {
  const { numOrders, numActiveOrders, priceDifference, priceScale, baseline } =
    strategy;
  if (
    isSymbol(numOrders) ||
    isSymbol(priceDifference) ||
    isSymbol(priceScale) ||
    isSymbol(longShort)
  )
    return {};
  const isGridBot = botType === BotType.GRID;
  const effectiveBaseline = getEffectiveStrategyBaseline(
    baseline,
    gridsData,
    botType
  );
  let result: StrategyPricesValidation | undefined;
  for (let direction of longShort || [LongShort.LONG]) {
    const reverse = direction === LongShort.SHORT;
    const sortOrder = getOrdersDefaultSortOrder(orderType, reverse, false);
    const isIncreasing = sortOrder === SortingOrder.ASCEND;

    const baseStrategyPrice = gridsData
      ? getGridsParsedBasePrice(gridsData, reverse) || 0
      : currentPrice;
    const ordersPrices = getOrdersPrices(
      numOrders,
      priceDifference,
      priceScale,
      effectiveBaseline,
      baseStrategyPrice,
      pricePrecision,
      orderType,
      reverse
    );

    const maxIndex = ordersPrices.length - 1;
    const invalidPrices = ordersPrices.some((price, index) => {
      if (price === 0 || price === MAX_PRICE) return true;
      const numActiveStrategyOrders =
        (isSymbol(numActiveOrders) ? undefined : numActiveOrders) ?? numOrders;
      if (!numActiveStrategyOrders) return false;
      let endIndex = index + numActiveStrategyOrders;
      endIndex = endIndex > maxIndex ? maxIndex : endIndex;
      if (endIndex === index) return false;
      return isPriceDifferenceInvalid(
        price,
        ordersPrices[endIndex],
        isIncreasing
      );
    });

    const hasConsecutiveGridsDistanceError = isGridBot
      ? ordersPrices.some((price, index) => {
          if (index < maxIndex) {
            const nextPrice = ordersPrices[index + 1];
            return (
              Math.abs((price - nextPrice) / price) * 100 <
              MIN_CONSECUTIVE_GRIDS_DISTANCE
            );
          }
          return false;
        })
      : false;

    const maxPriceDifference = Math.abs(
      toFixed(
        getPercentChange(
          ordersPrices[0],
          ordersPrices[ordersPrices.length - 1]
        ),
        2
      )
    );
    const getMaxPriceDifferenceError = () => {
      return `Some of the ${
        isGridBot ? "grids" : "DCA orders"
      } can't be created in ${
        reverse ? "short" : "long"
      } trades because their price might be too ${
        isIncreasing ? "high" : "low"
      }`;
    };

    const maxPriceDifferenceError = invalidPrices
      ? getMaxPriceDifferenceError()
      : undefined;

    if (invalidPrices || hasConsecutiveGridsDistanceError || !result) {
      result = {
        maxPriceDifference,
        maxPriceDifferenceError,
        invalidPrices,
        hasConsecutiveGridsDistanceError,
      };
    }
  }
  return result ?? {};
};

export const validateDefaultTakeProfit = (
  defaultTakeProfit: BaseBotFormValues["defaultTakeProfit"],
  longShort: BaseBotFormValues["longShort"]
) => {
  if (isSymbol(defaultTakeProfit) || !defaultTakeProfit) return undefined;
  const percent = defaultTakeProfit?.percent;
  if (isSymbol(percent) || isSymbol(longShort)) return undefined;
  if (!percent) return "You must provide a value";
  else if (
    longShort?.includes(LongShort.SHORT) &&
    parseFloat(percent) >= MAX_DEFAULT_TAKE_PROFIT_PERCENT
  )
    return `Value should be less than ${MAX_DEFAULT_TAKE_PROFIT_PERCENT}%`;
  else if (parseFloat(percent) === 0) {
    return `Value should be greater than 0`;
  }
};

export const validateBulkStopType = (
  stopType: BaseBotFormValues["stopType"],
  botType: BotType,
  botGeneralData?: BaseBotGeneralData
) => {
  if (botType === BotType.TRADING_VIEW_SIGNALS) return !stopType;
  if (!stopType) return botType === BotType.HEDGING;
  return (
    isSymbol(stopType) ||
    !!botGeneralData?.options?.["Stop Type"]?.[0]?.find(
      (option) => option.value === stopType
    )
  );
};

export const validateDefaultStopOrder = (
  percent: string | undefined,
  minStopPercent?: number,
  maxStopPercent?: number
) => {
  if (!percent) return "You must provide a value";
  const _percent = parseFloat(percent);
  if (minStopPercent !== undefined && _percent <= minStopPercent)
    return `Value should be greater than ${minStopPercent}%`;
  else if (maxStopPercent !== undefined && _percent > maxStopPercent)
    return `Value should be less than or equal to ${maxStopPercent}%`;
};

export const validateDefaultStop = (
  baseline: DefaultOrder<string>["baseline"],
  percent: DefaultOrder<string>["percent"],
  entries: StrategyOrder[],
  botType: BotType,
  isMarginExchange?: boolean,
  marginType?: MarginTypes | MultiValue
) => {
  if (isSymbol(baseline) || isSymbol(percent)) return undefined;
  const minStopPercent = getMinStopPercent(baseline, entries, botType);
  const maxStopPercent = getMaxStopPercent(
    isMarginExchange,
    botType,
    marginType
  );
  return validateDefaultStopOrder(percent, minStopPercent, maxStopPercent);
};

export const validateBaseStrategyData = (
  orderType: OrderTypes,
  strategy: Partial<ParsedDcaStrategyData>,
  dcaStrategyItem: ConfigurationItem | undefined,
  includeNumOrders: boolean,
  includeNumActiveOrders: boolean,
  ignorePrices: boolean,
  longShort: MultiValue | LongShort[] | undefined,
  currentPrice: number,
  pricePrecision: number,
  gridsData: GridsData | undefined,
  botType?: BotType
) => {
  const { numOrders, numActiveOrders, priceDifference, priceScale } = strategy;
  const {
    max_number_of_orders,
    max_number_of_active_orders,
    max_price_difference,
    max_price_scale,
  } = getItemValidations(dcaStrategyItem) || {};

  const unequalItemsInfo = {
    numOrders: isSymbol(numOrders),
    numActiveOrders: isSymbol(numActiveOrders),
    priceDifference: isSymbol(priceDifference),
    priceScale: isSymbol(priceScale),
  };

  const isNumOrdersValid =
    !includeNumOrders || validateStrategyValue(numOrders, max_number_of_orders);
  const isNumActiveOrdersValid =
    !includeNumActiveOrders ||
    validateStrategyValue(numActiveOrders, max_number_of_active_orders);
  const isPriceDifferenceValid =
    unequalItemsInfo.priceDifference ||
    ignorePrices ||
    validateStrategyValue(priceDifference, max_price_difference * 100);
  const isPriceScaleValid =
    unequalItemsInfo.priceScale ||
    ignorePrices ||
    validateStrategyValue(priceScale, max_price_scale);

  const pricesValidationData = validateStrategyPrices(
    orderType,
    strategy,
    longShort,
    currentPrice,
    pricePrecision,
    gridsData,
    botType
  );

  const isValid =
    isNumOrdersValid &&
    isNumActiveOrdersValid &&
    isPriceDifferenceValid &&
    isPriceScaleValid;

  return {
    isValid,
    isNumOrdersValid,
    isNumActiveOrdersValid,
    isPriceDifferenceValid,
    isPriceScaleValid,
    pricesValidationData,
  };
};

export const validateDcaStrategy = (
  orderType: OrderTypes,
  strategy: ParsedDcaStrategyData,
  isCustomStrategy: boolean | undefined,
  dcaStrategyItem: ConfigurationItem | undefined,
  includeNumOrders: boolean,
  includeNumActiveOrders: boolean,
  includeFirstOrderPercent: boolean,
  ignoreFirstOrderPercent: boolean,
  ignorePrices: boolean,
  longShort: MultiValue | LongShort[] | undefined,
  currentPrice: number,
  pricePrecision: number,
  gridsData: GridsData | undefined,
  botType?: BotType
) => {
  const { amountScale, firstOrderPercent } = strategy;
  const { max_first_order_percentage, max_amount_scale } =
    getItemValidations(dcaStrategyItem) || {};

  const unequalItemsInfo = {
    amountScale: isSymbol(amountScale),
    firstOrderPercent: isSymbol(firstOrderPercent),
  };

  const isAmountScaleValid =
    isCustomStrategy || validateStrategyValue(amountScale, max_amount_scale);
  const firstOrderPercentError = unequalItemsInfo.firstOrderPercent
    ? undefined
    : validateFirstOrderPercent(
        includeFirstOrderPercent,
        ignoreFirstOrderPercent,
        firstOrderPercent,
        max_first_order_percentage
      );

  const {
    isValid: baseIsValid,
    isNumOrdersValid,
    isNumActiveOrdersValid,
    isPriceDifferenceValid,
    isPriceScaleValid,
    pricesValidationData,
  } = validateBaseStrategyData(
    orderType,
    strategy,
    dcaStrategyItem,
    includeNumOrders,
    includeNumActiveOrders,
    ignorePrices,
    longShort,
    currentPrice,
    pricePrecision,
    gridsData,
    botType
  );

  const isValid =
    baseIsValid &&
    isNumOrdersValid &&
    isNumActiveOrdersValid &&
    !firstOrderPercentError &&
    !pricesValidationData.hasConsecutiveGridsDistanceError &&
    isAmountScaleValid &&
    isPriceDifferenceValid &&
    isPriceScaleValid;

  return {
    isValid,
    firstOrderPercentError,
    isNumOrdersValid,
    isNumActiveOrdersValid,
    isAmountScaleValid,
    isPriceDifferenceValid,
    isPriceScaleValid,
    unequalItemsInfo,
    pricesValidationData,
  };
};

export const getParsedStrategyValue = <
  T extends string | MultiValue | MultiToggleValue | undefined | ChannelValue
>(
  value: T
) => {
  if (isSymbol(value)) return value;
  return value ? parseFloat(value) : undefined;
};

export const validateBotStrategy = (
  botData: { configurations?: BotConfigurations } | undefined,
  botValues: BaseBotFormValues,
  orderType: OrderTypes,
  currentPrice: number,
  pricePrecision: number,
  botType: BotType
) => {
  const { entryStrategy, takeProfitStrategy, gridsData, hedgingGridsData } =
    botValues;
  const strategyGridsData =
    botType === BotType.HEDGING ? hedgingGridsData : gridsData;
  const strategy =
    orderType === OrderTypes.ENTRY ? entryStrategy : takeProfitStrategy;
  if (isSymbol(strategy) || isSymbol(botValues.longShort)) return;
  const isDca = isDcaStrategy(strategy);
  const isCustom = isCustomStrategy(strategy);
  const isEntry = orderType === OrderTypes.ENTRY;
  const strategiesItem = getConfigurationItem(
    botData,
    isEntry ? "Entry Strategy" : "Take-Profit Strategy"
  );
  let strategyData: any = {
    name: "",
    numActiveOrders: strategy?.number_of_active_orders,
    priceDifference: strategy?.price_difference,
    priceScale: strategy?.price_scale,
  };
  if (isDca) {
    strategyData = {
      ...strategyData,
      numOrders: strategy?.number_of_orders,
      amountScale: strategy?.amount_scale,
      firstOrderPercent: strategy?.first_order_percentage,
    };
  } else if (isCustom && strategy?.targets && !isSymbol(strategy?.targets)) {
    strategyData = {
      ...strategyData,
      numOrders: findLastNonZeroValue(strategy.targets),
    };
  }
  return validateDcaStrategy(
    orderType,
    strategyData,
    !isDca,
    strategiesItem,
    false,
    isEntry && botType !== BotType.TRADING_VIEW_SIGNALS,
    isEntry && isDca,
    false,
    botType === BotType.SIGNAL &&
      !strategyData.priceDifference &&
      !strategyData.priceScale,
    botValues.longShort,
    currentPrice,
    pricePrecision,
    strategyGridsData,
    botType
  );
};

export const validateBotStrategies = (
  botValues: BaseBotFormValues,
  botData: { configurations?: BotConfigurations } | undefined,
  currentPrice: number,
  pricePrecision: number,
  botType: BotType,
  checkedFields: Set<ToggleField>
) => {
  const isEntryStrategyEnabled =
    botType === BotType.HEDGING
      ? isHedgingRangeMode(botValues) && !isHedgingRangeStrategyMixed(botValues)
      : checkedFields.has(ToggleField.ENTRIES);
  const entryValidationData =
    botValues.entryStrategy && isEntryStrategyEnabled
      ? validateBotStrategy(
          botData,
          botValues,
          OrderTypes.ENTRY,
          currentPrice,
          pricePrecision,
          botType
        )
      : undefined;
  const takeProfitValidationData =
    botValues.takeProfitStrategy &&
    checkedFields.has(ToggleField.TAKE_PROFIT_STRATEGY)
      ? validateBotStrategy(
          botData,
          botValues,
          OrderTypes.TAKE_PROFIT,
          currentPrice,
          pricePrecision,
          botType
        )
      : undefined;
  const entriesValid =
    !entryValidationData ||
    (entryValidationData.isValid &&
      !entryValidationData.pricesValidationData.invalidPrices);
  const takeProfitsValid =
    !takeProfitValidationData ||
    (takeProfitValidationData.isValid &&
      !takeProfitValidationData.pricesValidationData.invalidPrices);
  return { entriesValid, takeProfitsValid };
};

const getOptionValueToOptionMapping = (options: NumberOption[]) => {
  return options?.reduce((acc: Record<string, NumberOption>, option) => {
    acc[option.value.toString()] = option;
    return acc;
  }, {});
};

const removeInvalidOptions = (
  optionValueToOptionMapping: Record<string, NumberOption>,
  getOptionsFromSymbol: (symbol: string) => NumberOption[] | undefined,
  symbols: string[]
) => {
  symbols.forEach((symbol) => {
    const optionValues = new Set(
      getOptionsFromSymbol(symbol)?.map((option) => option.value.toString())
    );
    Object.keys(optionValueToOptionMapping || {}).forEach((optionValue) => {
      if (!optionValues.has(optionValue)) {
        delete optionValueToOptionMapping?.[optionValue];
      }
    });
  });
};

const getOptions = (
  getOptionsFromSymbol: (symbol: string) => NumberOption[] | undefined,
  symbols: string[] | undefined
) => {
  if (symbols?.length) {
    const firstSymbolMultipleOptions = getOptionsFromSymbol(symbols[0]);
    const symbolOptions =
      firstSymbolMultipleOptions &&
      getOptionValueToOptionMapping(firstSymbolMultipleOptions);
    symbolOptions &&
      removeInvalidOptions(symbolOptions, getOptionsFromSymbol, symbols);
    return symbolOptions ? Object.values(symbolOptions) : [];
  }
  return [];
};

export const getBotMarginOptions = (
  botOptions: BotOptions | undefined,
  symbols: string[] | undefined | MultiValue
) => {
  if (isSymbol(symbols)) return {};
  const longShortOptions = botOptions?.["Long/Short"]?.[0];
  const marginTypeMapping = botOptions?.["Margin Type"]?.[0];
  const multiplierMapping = botOptions?.["Multiplier"]?.[0];

  let marginTypeOptions;
  if (marginTypeMapping) {
    const getSymbolMarginTypeOptions = (symbol: string) =>
      marginTypeMapping?.[symbol]?.[0];
    marginTypeOptions = getOptions(getSymbolMarginTypeOptions, symbols);
  }

  let multiplierOptions;
  if (multiplierMapping) {
    const getSymbolMultiplierOptions = (symbol: string) =>
      multiplierMapping?.[symbol]?.[0];
    const options = getOptions(getSymbolMultiplierOptions, symbols);
    multiplierOptions = options
      .slice()
      .sort((a, b) => sortByNumberProperty(a.value, b.value));
  }

  return {
    marginTypeOptions,
    multiplierOptions,
    longShortOptions,
  };
};

export const getBulkCreateMarginOptions = (
  overlapData?: OverlapData
): {
  longShortOptions: NumberOption[] | undefined;
  marginTypeOptions: NumberOption[] | undefined;
  multiplierOptions: NumberOption[] | undefined;
} => {
  const getFilteredOptions = (
    key: "longShort" | "marginType" | "multiplier"
  ) => {
    return overlapData?.[key]?.allOptions;
  };

  return {
    longShortOptions: getFilteredOptions("longShort"),
    marginTypeOptions: getFilteredOptions("marginType"),
    multiplierOptions: getFilteredOptions("multiplier"),
  };
};

type LeverageTranslate = (
  key: TranslationKeys<LanguageNameSpaces.BOTS>,
  options?: Record<string, unknown>
) => string;

const validateLeverageValue = (
  options: NumberOption[] | undefined,
  value: number | undefined,
  displayName: string,
  displayValue: string | undefined,
  symbol: string | undefined,
  account: ClientData | undefined,
  botFormType: BotFormType,
  t: LeverageTranslate
) => {
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  if (!options?.length) return;
  if (!value) {
    return t("messages.errors.leverage_value_required", { field: displayName });
  }
  if (!options.find((option) => option.value === value)) {
    return t("messages.errors.leverage_value_invalid", {
      field: displayName,
      value: displayValue,
      symbol,
      exchange: isBulkCreate
        ? t("messages.errors.some_selected_exchanges")
        : account?.exchange,
    });
  }
};

export const validateLeverage = (
  marginTypeOptions: NumberOption[] | undefined,
  multiplierOptions: NumberOption[] | undefined,
  longShortOptions: NumberOption[] | undefined,
  longShort: LongShort | undefined,
  marginType: MarginTypes | undefined,
  multiplier: number | undefined,
  symbol: string | undefined,
  account: ClientData | undefined,
  botType: BotType,
  botFormType: BotFormType,
  t: LeverageTranslate
) => {
  const isSignalsBot = botType === BotType.SIGNAL;
  const errorMessages: string[] = [];
  const longShortError = validateLeverageValue(
    longShortOptions,
    longShort,
    t("leverage_fields.direction"),
    longShort && translateOptionName(LONG_SHORT_TO_DISPLAY[longShort]),
    symbol,
    account,
    botFormType,
    t
  );
  const marginTypeError = isSignalsBot
    ? undefined
    : validateLeverageValue(
        marginTypeOptions,
        marginType,
        t("leverage_fields.margin_type"),
        marginType && translateOptionName(MARGIN_TYPE_TO_DISPLAY[marginType]),
        symbol,
        account,
        botFormType,
        t
      );
  const multiplierError = isSignalsBot
    ? undefined
    : validateLeverageValue(
        multiplierOptions,
        multiplier,
        t("leverage_fields.multiplier"),
        isNumber(multiplier) ? multiplier.toString() : undefined,
        symbol,
        account,
        botFormType,
        t
      );
  [longShortError, marginTypeError, multiplierError].forEach((errorMessage) => {
    if (errorMessage) {
      errorMessages.push(errorMessage);
    }
  });
  return errorMessages;
};

export const validateBotAmount = (
  parsedValidationEntries: StrategyOrder[] | undefined,
  botAmountData: BotAmountData | undefined,
  isFetchingAmounts: boolean,
  isEdit: boolean,
  entryStrategy: StrategyData | undefined,
  initialBotAmountData: InitialBotAmountData | undefined,
  currentPrice?: number,
  botType?: BotType,
  useSymbolFromAlert?: boolean | MultiValue
) => {
  if (
    botAmountData &&
    getIsPercentAmountType(botAmountData.amountType) &&
    (botType === BotType.SIGNAL || useSymbolFromAlert)
  ) {
    if (
      !botAmountData.amountValue ||
      botAmountData.amountValue > 100 ||
      (botAmountData.amountPercentageBaseType ===
        PercentageBaseType.CUSTOM_AMOUNT &&
        !botAmountData.amountCustomBaseValue)
    ) {
      return [Error.GENERAL_AMOUNT];
    }
  }
  if (
    botType === BotType.HEDGING &&
    botAmountData &&
    botAmountData.mainCoinAmount > botAmountData.mainCoinAvailableAmount
  ) {
    return [Error.GENERAL_AMOUNT];
  }
  if (
    !parsedValidationEntries ||
    isFetchingAmounts ||
    !currentPrice ||
    !botAmountData ||
    useSymbolFromAlert
  )
    return [];

  const errors: Error[] = [];
  let hasInsufficientAmountError = false;
  let hasMinEntryOrderAmountError = false;
  let hasActiveEntriesAmountError = false;
  if (!isEdit) {
    const firstEntry = parsedValidationEntries[0];
    if (
      firstEntry?.minAmount &&
      botAmountData.mainCoinAvailableAmount < firstEntry.minAmount
    ) {
      errors.push(Error.INSUFFICIENT_AMOUNT);
      hasInsufficientAmountError = true;
    }
    const numActiveOrders = entryStrategy?.number_of_active_orders;
    if (
      botType !== BotType.HEDGING &&
      !hasInsufficientAmountError &&
      numActiveOrders &&
      !isSymbol(numActiveOrders)
    ) {
      if (
        initialBotAmountData &&
        initialBotAmountData.validationActiveMainCoinAmount >
          botAmountData.mainCoinAvailableAmount
      ) {
        hasActiveEntriesAmountError = true;
        errors.push(Error.ACTIVE_ENTRIES_AMOUNT);
      }
    }
  }

  if (
    !hasInsufficientAmountError &&
    parsedValidationEntries.some((order) => !order.isValid)
  ) {
    hasMinEntryOrderAmountError = true;
    errors.push(Error.MIN_ENTRY_ORDER_AMOUNT);
  }

  if (
    (hasActiveEntriesAmountError && !hasMinEntryOrderAmountError) ||
    (!hasActiveEntriesAmountError && hasMinEntryOrderAmountError)
  ) {
    errors.push(Error.GENERAL_AMOUNT);
  }

  return errors;
};

export const validateCustomFirstEntry = (
  entryStrategy: StrategyData | undefined
) => {
  if (!isDcaStrategy(entryStrategy)) return true;
  let isValid = true;
  if (
    !!entryStrategy.first_order_percentage &&
    entryStrategy.number_of_orders === 1
  ) {
    isValid = false;
  }
  return isValid;
};

const convertEmptyValue = (fieldValue: any) => {
  if (fieldValue === channelValue) return undefined;
  if (isSymbol(fieldValue)) return empty;
  return fieldValue;
};

const getParsedToggleFieldValue = (
  botType: BotType,
  checkedFields: Set<ToggleField>,
  formValues: BotFormValues,
  fieldValue: any,
  toggleField: ToggleField,
  defaultValue: any = null,
  isToggleFieldChild?: boolean
) => {
  const isSignalsBot = botType === BotType.SIGNAL;
  const isParentChecked = getParentFieldChecked(
    botType,
    formValues,
    checkedFields,
    toggleField
  );
  const isFieldToggledOff = !isParentChecked || !checkedFields.has(toggleField);
  if (isFieldToggledOff)
    return isSignalsBot &&
      (ALL_OVERRIDES_TOGGLE_FIELDS.has(toggleField) ||
        toggleField === ToggleField.TAKE_PROFIT_DYNAMIC) &&
      !isToggleFieldChild
      ? off
      : defaultValue;
  else if (fieldValue === channelValue) return defaultValue;
  else if (isSymbol(fieldValue)) return empty;
  return fieldValue || defaultValue;
};

export const getBaseParsedFormData = (
  formValues: BotFormValues,
  botType: BotType,
  checkedFields: Set<ToggleField>,
  overrideFields: Set<OverrideFieldType>,
  exchangeSymbols?: string[],
  isChannelConfiguration?: boolean
): Partial<CreateBotRequest> => {
  const {
    name,
    accounts,
    exchanges,
    symbols,
    includeNewSymbols,
    longShort,
    marginType,
    multiplier,
    stopTimeout,
    trailingEntry,
    trailingTakeProfit,
    trailingTakeProfitLastTargetOnly,
    trailingStopData,
    defaultStop,
    defaultTakeProfit,
    entryStrategy,
    takeProfitStrategy,
    numberOfContinuousStopsTillBotStop,
    amountType,
    amountValue,
    amountPercentageBaseType,
    amountCustomBaseValue,
    multiSymbolMarginData,
    advancedSettings,
    gridsData,
    hedgingMode,
    hedgingGridsData,
    cancelOnProfitBeforeEntry,
    entryGracePercent,
    entryGraceBaseline,
    stopType,
    stopLimitPriceReduction,
    subGroupBotConfiguration,
    subGroup,
    alternativeUsdPairs,
    takeProfitGracePercent,
    entryGraceTargetExchange,
    takeProfitGraceTargetExchange,
    operationHoursEvents,
    useSymbolFromAlert,
    keywordToSymbolMapping,
    longHedgingRatio,
  } = formValues;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isTradingViewBot = botType === BotType.TRADING_VIEW;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTvBots = isTradingViewBot || isTradingViewSignalsBot;

  const getParsedValue = (
    fieldValue: string | MultiValue | ChannelValue | undefined
  ) => {
    if (isSymbol(fieldValue)) return empty;
    const parsedValue = parseFloat(fieldValue === undefined ? "" : fieldValue);
    return isNaN(parsedValue) ? null : parsedValue;
  };

  let parsedStopLimitPriceReduction: Empty | number | null =
    isSymbol(stopType) || stopType === StopType.LIMIT
      ? getParsedValue(stopLimitPriceReduction)
      : null;
  if (typeof parsedStopLimitPriceReduction === "number") {
    parsedStopLimitPriceReduction = 1 - parsedStopLimitPriceReduction / 100;
  }

  let parsedLongShort = convertEmptyValue(longShort);
  if (!isSymbol(parsedLongShort) && parsedLongShort?.length === 2) {
    parsedLongShort = undefined;
  } else if (parsedLongShort?.length === 1) {
    parsedLongShort = parsedLongShort[0];
  }

  const getBlacklistSymbols = () => {
    if (!isSignalsBot || !includeNewSymbols || isSymbol(symbols)) return;
    return difference(exchangeSymbols, symbols);
  };

  const getSymbols = () => {
    if (isTvBots && useSymbolFromAlert) {
      return isSymbol(useSymbolFromAlert) ? empty : null;
    }
    return includeNewSymbols ? undefined : convertEmptyValue(symbols);
  };

  const getDefaultTakeProfit = () => {
    const parsedDefaultOrder = getParsedDefaultOrder(
      defaultTakeProfit ?? DEFAULT_TAKE_PROFIT
    );
    if (isSignalsBot && parsedDefaultOrder && !isSymbol(parsedDefaultOrder)) {
      return {
        baseline: getParsedToggleFieldValue(
          botType,
          checkedFields,
          formValues,
          parsedDefaultOrder.baseline,
          ToggleField.TAKE_PROFIT_DYNAMIC
        ),
        percent: getParsedToggleFieldValue(
          botType,
          checkedFields,
          formValues,
          parsedDefaultOrder.percent,
          ToggleField.TAKE_PROFITS
        ),
      };
    }
    return getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      parsedDefaultOrder,
      ToggleField.TAKE_PROFITS
    );
  };

  const overrideData = Object.values(OverrideFieldType).reduce(
    (acc, fieldType) => {
      acc[fieldType] = overrideFields.has(fieldType);
      return acc;
    },
    {} as OverrideData
  );

  const _getParsedStrategy = (
    strategy: BaseBotFormValues["entryStrategy"],
    toggleField: ToggleField
  ) => {
    const parsedStrategy = getParsedStrategy(
      botType,
      strategy,
      isChannelConfiguration
    );
    if (isSignalsBot) return parsedStrategy;
    return getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      parsedStrategy,
      toggleField
    );
  };

  const getAmountConfiguration = () => {
    const amountConfiguration = amountType
      ? {
          type: convertEmptyValue(amountType),
          value: convertEmptyValue(
            getIsPercentAmountType(amountType)
              ? percentToRatio(amountValue || 0)
              : amountValue || 0
          ),
          percentage_base_type: amountPercentageBaseType,
          custom_base_value: amountCustomBaseValue
            ? parseFloat(amountCustomBaseValue)
            : undefined,
        }
      : undefined;
    if (isChannelConfiguration || isTradingViewSignalsBot) {
      return getParsedToggleFieldValue(
        botType,
        checkedFields,
        formValues,
        amountConfiguration,
        ToggleField.AMOUNT_PER_TRADE
      );
    }
    return amountConfiguration;
  };

  const parsedTrailingTakeProfit = getParsedToggleFieldValue(
    botType,
    checkedFields,
    formValues,
    trailingTakeProfit ? percentToRatio(trailingTakeProfit) : undefined,
    ToggleField.TRAILING_TAKE_PROFIT
  );
  const getParsedHedgingExposureRatio = (value?: string | MultiValue) => {
    if (isSymbol(value)) return empty;
    const parsedValue = getParsedPercent(value);
    return parsedValue !== undefined
      ? toFixed(parsedValue / 100, 4)
      : undefined;
  };
  const hedgingRangeStrategyIsMixed =
    isHedgingBot &&
    isHedgingRangeStrategyMixed({
      hedgingMode,
      entryStrategy,
      hedgingGridsData,
    });
  const hedgingModeIsMixed = isHedgingBot && isSymbol(hedgingMode);
  const hedgingModeIsFixed = isHedgingBot && hedgingMode === HedgingMode.FIXED;
  const hedgingModeIsRange = isHedgingBot && hedgingMode === HedgingMode.RANGE;
  const hedgingStrategyIsMixed =
    hedgingModeIsMixed || hedgingRangeStrategyIsMixed;
  const getHedgingModeValue = <FixedValue, RangeValue>(
    fixedValue: FixedValue,
    rangeValue: RangeValue
  ) => {
    if (hedgingModeIsMixed) return empty;
    if (hedgingModeIsFixed) return fixedValue;
    if (hedgingModeIsRange) return rangeValue;
    return undefined;
  };
  const parsedLongHedgingRatio = isSymbol(longHedgingRatio)
    ? empty
    : longHedgingRatio !== undefined
    ? toFixed(longHedgingRatio / 100, 4)
    : undefined;

  return {
    ...overrideData,
    bot_type: botType,
    dca_bot_name: convertEmptyValue(name),
    account_ids: isTradingViewSignalsBot
      ? undefined
      : isSymbol(accounts)
      ? empty
      : accounts.map((account) => account?.client_id),
    exchanges: isTradingViewSignalsBot
      ? convertEmptyValue(exchanges)
      : undefined,
    symbols: getSymbols(),
    blacklist_symbols: getBlacklistSymbols(),
    long_short: parsedLongShort,
    margin_type: convertEmptyValue(marginType || null),
    multiplier: convertEmptyValue(multiplier),
    stop_timeout: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      stopTimeout,
      ToggleField.STOP_TIMEOUT,
      0
    ),
    trailing_entry: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      trailingEntry ? percentToRatio(trailingEntry) : undefined,
      ToggleField.TRAILING_ENTRY
    ),
    trailing_take_profit: parsedTrailingTakeProfit,
    trailing_take_profit_last_target_only:
      !!trailingTakeProfitLastTargetOnly &&
      typeof parsedTrailingTakeProfit === "number",
    trailing_stop: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      getParsedTrailingStop(trailingStopData),
      ToggleField.TRAILING_STOP
    ),
    default_stop: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      getParsedDefaultOrder(defaultStop ?? DEFAULT_STOP),
      ToggleField.STOP
    ),
    default_take_profit: getDefaultTakeProfit(),
    entry_strategy: hedgingStrategyIsMixed
      ? empty
      : hedgingModeIsFixed
      ? null
      : _getParsedStrategy(entryStrategy, ToggleField.ENTRIES),
    take_profit_strategy: _getParsedStrategy(
      takeProfitStrategy,
      ToggleField.TAKE_PROFIT_STRATEGY
    ),
    amount_configuration: getAmountConfiguration(),
    number_of_continuous_stops_till_bot_stop: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      getParsedValue(numberOfContinuousStopsTillBotStop),
      ToggleField.STOP,
      null,
      true
    ),
    number_of_trades_till_bot_stop: getParsedValue(
      advancedSettings.number_of_trades_till_bot_stop
    ),
    cooldown_between_opening_trades: getParsedValue(
      advancedSettings.cooldown_between_opening_trades
    ),
    cooldown_after_trade_close: getParsedValue(
      advancedSettings.cooldown_after_trade_close
    ),
    number_of_simultaneous_trades: getParsedValue(
      advancedSettings.number_of_simultaneous_trades
    ),
    min_symbol_price: getParsedValue(advancedSettings.min_symbol_price),
    min_symbol_volume: getParsedValue(advancedSettings.min_symbol_volume),
    concurrent_max_usd_invested: getParsedValue(
      advancedSettings.concurrent_max_usd_invested
    ),
    cooldown_between_opening_trades_per_symbol: getParsedValue(
      advancedSettings.cooldown_between_opening_trades_per_symbol
    ),
    number_of_simultaneous_trades_per_symbol: getParsedValue(
      advancedSettings.number_of_simultaneous_trades_per_symbol
    ),
    time_stop_seconds: getParsedValue(advancedSettings.time_stop_seconds),
    auto_cancel_timeout_seconds: getParsedValue(
      advancedSettings.auto_cancel_timeout_seconds
    ),
    symbol_to_leverage_data: isSignalsBot
      ? getParsedToggleFieldValue(
          botType,
          checkedFields,
          formValues,
          getParsedMultiSymbolMarginData(multiSymbolMarginData),
          ToggleField.MARGIN
        )
      : undefined,
    max_grid_price: isGridBot
      ? getGridsParsedBasePrice(gridsData, getIsReverse(longShort))
      : hedgingStrategyIsMixed
      ? empty
      : hedgingModeIsRange
      ? getGridsParsedBasePrice(hedgingGridsData, false)
      : hedgingModeIsFixed
      ? null
      : undefined,
    cancel_on_profit_before_entry: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      cancelOnProfitBeforeEntry,
      ToggleField.CLOSE_ON_TP_BEFORE_ENTRY
    ),
    sub_group_ids: subGroup ? [subGroup.id] : undefined,
    channel_configuration_id: subGroupBotConfiguration
      ? subGroupBotConfiguration.id
      : undefined,
    stop_type: convertEmptyValue(stopType),
    stop_limit_price_reduction: parsedStopLimitPriceReduction,
    entry_grace_percent: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      percentToRatio(entryGracePercent),
      ToggleField.ENTRY_GRACE_PERCENT
    ),
    entry_grace_baseline: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      entryGraceBaseline,
      ToggleField.ENTRY_GRACE_PERCENT
    ),
    skip_without_stoploss: checkedFields.has(
      ToggleField.SKIP_WITHOUT_STOP_LOSS
    ),
    follow_futures_as_spot:
      botType === BotType.SIGNAL &&
      !isChannelConfiguration &&
      !isSymbol(formValues.followFuturesAsSpot) &&
      !isSymbol(accounts) &&
      accounts?.length === 1
        ? formValues.followFuturesAsSpot
        : undefined,
    alternative_usd_pairs: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      alternativeUsdPairs,
      ToggleField.ALTERNATIVE_USD_PAIRS
    ),
    take_profit_grace_percent: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      percentToRatio(takeProfitGracePercent),
      ToggleField.TAKE_PROFIT_GRACE_PERCENT
    ),
    entry_grace_target_exchange: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      entryGraceTargetExchange,
      ToggleField.ENTRY_GRACE_PERCENT
    ),
    take_profit_grace_target_exchange: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      takeProfitGraceTargetExchange,
      ToggleField.TAKE_PROFIT_GRACE_PERCENT
    ),
    blacklisted_operation_hours: getParsedToggleFieldValue(
      botType,
      checkedFields,
      formValues,
      operationHoursEvents,
      ToggleField.OPERATION_HOURS
    ),
    keyword_to_symbol_mapping: keywordToSymbolMapping,
    long_exposure_ratio: getHedgingModeValue(parsedLongHedgingRatio, null),
    lower_price_long_exposure_ratio: getHedgingModeValue(
      null,
      getParsedHedgingExposureRatio(
        hedgingGridsData?.lowerPriceLongExposureRatio
      )
    ),
    upper_price_long_exposure_ratio: getHedgingModeValue(
      null,
      getParsedHedgingExposureRatio(
        hedgingGridsData?.upperPriceLongExposureRatio
      )
    ),
  };
};

export const getDefaultBotDcaStrategy = (
  botData: { configurations?: BotConfigurations } | undefined,
  orderType: OrderTypes
): StrategyData => {
  const strategyItem = getConfigurationItem(
    botData,
    orderType === OrderTypes.ENTRY ? "Entry Strategy" : "Take-Profit Strategy"
  );
  const strategiesItem = getItemByName(DCA_STRATEGIES_ITEM_NAME, strategyItem);
  const strategyTemplate = getCreateItem(strategiesItem);
  return getItemValue(strategyTemplate) || {};
};

export const getDefaultBotStrategy = (
  botData: { configurations?: BotConfigurations } | undefined,
  orderType: OrderTypes,
  isSignalsBot: boolean
): StrategyData => {
  let strategyData: Partial<StrategyData> = {};
  if (isSignalsBot) {
    return {
      strategy_style: StrategyStyleType.BUILT_IN,
      builtin_strategy_type: StrategyType.EVENLY_DIVIDED,
    };
  } else {
    strategyData = getDefaultBotDcaStrategy(botData, orderType);
  }
  const {
    number_of_orders,
    amount_scale,
    first_order_percentage,
    number_of_active_orders,
    price_difference,
    price_scale,
  } = strategyData as Partial<DCAStrategyData>;
  const baseOrderData: BaseStrategyData = {
    price_difference:
      typeof price_difference === "number"
        ? toFixed(price_difference * 100, 2)
        : undefined,
    price_scale,
    number_of_active_orders: number_of_active_orders || null,
  };
  return {
    ...baseOrderData,
    strategy_style: StrategyStyleType.DCA,
    number_of_orders,
    amount_scale,
    first_order_percentage:
      typeof first_order_percentage === "number"
        ? toFixed(first_order_percentage * 100, 2)
        : undefined,
  };
};

const safeNumberToString = (intValue?: number | null | MultiValue) => {
  if (isSymbol(intValue)) return intValue;
  return intValue?.toString();
};

export const getBotAmountTypeAndValue = (
  bot: BaseBotData | BotData,
  hasBaselineFeatureFlag?: boolean
) => {
  const amountType = getOptionsItemValue(bot, "Amount Type");
  let amountValue = getOptionsItemValue(bot, "Amount Value");
  const isPercentAmount = getIsPercentAmountType(amountType);
  if (isPercentAmount && !isNaN(amountValue!)) {
    amountValue = toFixed(amountValue! * 100, 2);
  }
  let amountPercentageBaseType = getOptionsItemValue(
    bot,
    "Amount Percentage Base Type"
  );
  const amountCustomBaseValue = getOptionsItemValue(
    bot,
    "Amount Custom Base Value"
  )?.toString();
  if (hasBaselineFeatureFlag && isPercentAmount && !amountPercentageBaseType) {
    amountPercentageBaseType = PercentageBaseType.PAIR_AMOUNT;
  }
  return {
    amountType,
    amountValue,
    amountPercentageBaseType,
    amountCustomBaseValue,
  };
};

export const getStopLimitPriceReduction = (botData: BotData | BaseBotData) => {
  if (typeof botData.stop_limit_price_reduction === "number") {
    return toFixed(
      (1 - botData.stop_limit_price_reduction) * 100,
      2
    ).toString();
  } else if (!botData.stop_limit_price_reduction) {
    const stopType = getBotStopType(botData);
    if (stopType === StopType.LIMIT) {
      return DEFAULT_STOP_LIMIT_PRICE_REDUCTION.toString();
    }
  }
};

const addStrategyToggleFields = (
  strategy: BaseBotFormValues["entryStrategy"],
  toggleFields: ToggleField[],
  isEntry: boolean
) => {
  if (strategy)
    toggleFields.push(
      isEntry ? ToggleField.ENTRIES : ToggleField.TAKE_PROFIT_STRATEGY
    );
  if (strategy && !isSymbol(strategy)) {
    if (
      "first_order_percentage" in strategy &&
      !!strategy.first_order_percentage
    )
      toggleFields.push(ToggleField.FIRST_ENTRY);
    if (strategy.price_difference || strategy.price_scale) {
      toggleFields.push(
        isEntry
          ? ToggleField.ENTRY_PRICE_STRATEGY
          : ToggleField.TAKE_PROFIT_PRICE_STRATEGY
      );
    }
    if (
      ("targets" in strategy && !!strategy.targets) ||
      ("amount_scale" in strategy && !!strategy.amount_scale) ||
      ("first_order_percentage" in strategy &&
        !!strategy.first_order_percentage) ||
      ("builtin_strategy_type" in strategy && !!strategy.builtin_strategy_type)
    ) {
      toggleFields.push(
        isEntry ? ToggleField.ENTRY_RATIOS : ToggleField.TAKE_PROFIT_RATIOS
      );
    }
    if ("number_of_orders" in strategy && !!strategy.number_of_orders) {
      toggleFields.push(
        isEntry
          ? ToggleField.ENTRY_NUM_ORDERS
          : ToggleField.TAKE_PROFIT_NUM_ORDERS
      );
    }
    if (
      "number_of_active_orders" in strategy &&
      !!strategy.number_of_active_orders
    ) {
      toggleFields.push(
        isEntry
          ? ToggleField.ENTRY_NUM_ACTIVE_ORDERS
          : ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS
      );
    }
  }
};

export const getBotValues = (
  botType: BotType,
  bot: BotData | BaseBotData,
  extraOptions?: {
    getAccountById?: (accountId: number) => ClientData | undefined;
    getExchangeSymbols?: (exchange: string) => string[];
    getAccountActiveSymbols?: (accountId?: number) => string[] | undefined;
    useDefaultOrderDefaultValue?: boolean;
    pricePrecision?: number;
    hasAmountPercentageFeatureFlag?: boolean;
  },
  isChannelConfiguration?: boolean
): [BaseBotFormValues, Set<ToggleField>, Set<OverrideFieldType>] => {
  const toggleFields: ToggleField[] = [];
  const overrideFields: OverrideFieldType[] = [];
  const isSignalsBot = botType === BotType.SIGNAL;
  const isHedgingBot = botType === BotType.HEDGING;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot = botType === BotType.TRADING_VIEW;
  if (isSignalsBot) {
    Object.values(OverrideFieldType).forEach((fieldType) => {
      if (bot[fieldType]) overrideFields.push(fieldType);
    });
  } else {
    toggleFields.push(ToggleField.ENTRY_PRICE_STRATEGY);
    toggleFields.push(ToggleField.ENTRY_RATIOS);
    toggleFields.push(ToggleField.ENTRY_NUM_ORDERS);
    toggleFields.push(ToggleField.ENTRY_NUM_ACTIVE_ORDERS);
    toggleFields.push(ToggleField.TAKE_PROFIT_PRICE_STRATEGY);
    toggleFields.push(ToggleField.TAKE_PROFIT_RATIOS);
    toggleFields.push(ToggleField.TAKE_PROFIT_NUM_ORDERS);
    toggleFields.push(ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS);
  }
  const isGridBot = botType === BotType.GRID;
  const {
    getAccountById,
    getExchangeSymbols,
    getAccountActiveSymbols,
    useDefaultOrderDefaultValue,
    pricePrecision,
    hasAmountPercentageFeatureFlag,
  } = extraOptions || {};

  const getToggleFieldValue = (fieldValue: any, toggleField: ToggleField) =>
    getOverrideValue(bot, toggleField, fieldValue, isChannelConfiguration);

  const trailingEntry = getToggleFieldValue(
    getConfigurationItemValue(bot, "Trailing Entry"),
    ToggleField.TRAILING_ENTRY
  );
  const trailingTakeProfit = getToggleFieldValue(
    getConfigurationItemValue(bot, "Trailing Take-Profit"),
    ToggleField.TRAILING_TAKE_PROFIT
  );
  if (trailingEntry) toggleFields.push(ToggleField.TRAILING_ENTRY);
  if (trailingTakeProfit) toggleFields.push(ToggleField.TRAILING_TAKE_PROFIT);
  const parsedTrailingStop = getBotParsedTrailingStop(bot);
  const isTrailingStopUndefined =
    !bot.trailing_stop || !parsedTrailingStop?.type;
  let trailingStopData: BaseBotFormValues["trailingStopData"] =
    getOverrideValue(
      bot,
      ToggleField.TRAILING_STOP,
      isTrailingStopUndefined ? undefined : parsedTrailingStop
    );

  if (isSignalsBot && isSymbol(trailingStopData)) {
    toggleFields.push(ToggleField.TRAILING_STOP);
    trailingStopData = channelValue;
  } else if (!isTrailingStopUndefined) {
    toggleFields.push(ToggleField.TRAILING_STOP);
  }

  const entryStrategy = getStrategy(
    bot,
    OrderTypes.ENTRY,
    botType,
    isChannelConfiguration
  );
  addStrategyToggleFields(entryStrategy, toggleFields, true);

  const takeProfitStrategy = isSignalsBot
    ? getStrategy(bot, OrderTypes.TAKE_PROFIT, botType, isChannelConfiguration)
    : getToggleFieldValue(
        getStrategy(
          bot,
          OrderTypes.TAKE_PROFIT,
          botType,
          isChannelConfiguration
        ),
        ToggleField.TAKE_PROFIT_STRATEGY
      );
  addStrategyToggleFields(takeProfitStrategy, toggleFields, false);

  const [defaultStop, isDefaultStopOn] = getDefaultOrder(
    bot,
    OrderTypes.STOP,
    useDefaultOrderDefaultValue
  );
  if (isDefaultStopOn) toggleFields.push(ToggleField.STOP);

  const [defaultTakeProfit, isDefaultTakeProfitOn, isDynamicTakeProfitOn] =
    getDefaultOrder(
      bot,
      OrderTypes.TAKE_PROFIT,
      useDefaultOrderDefaultValue
    ) as unknown as [BaseBotFormValues["defaultTakeProfit"], boolean, boolean];
  if (isDefaultTakeProfitOn) toggleFields.push(ToggleField.TAKE_PROFITS);
  if (isDynamicTakeProfitOn) toggleFields.push(ToggleField.TAKE_PROFIT_DYNAMIC);

  const stopTimeout = getOptionsItemValue(bot, "Stop Timeout");
  if (stopTimeout) toggleFields.push(ToggleField.STOP_TIMEOUT);
  let entryGracePercent = getOptionsItemValue(bot, "Entry Grace Percentage");
  if (entryGracePercent) {
    toggleFields.push(ToggleField.ENTRY_GRACE_PERCENT);
    entryGracePercent = toFixed(entryGracePercent * 100, 2);
  }
  let takeProfitGracePercent = getOptionsItemValue(
    bot,
    "Take Profit Grace Percentage"
  );
  if (takeProfitGracePercent) {
    toggleFields.push(ToggleField.TAKE_PROFIT_GRACE_PERCENT);
    takeProfitGracePercent = toFixed(takeProfitGracePercent * 100, 2);
  }

  const operationHoursEvents = bot.blacklisted_operation_hours;
  if (operationHoursEvents?.length) {
    toggleFields.push(ToggleField.OPERATION_HOURS);
  }

  const [multiSymbolMarginData, isMarginOn] = getBotMultiSymbolMarginData(bot);
  if (isMarginOn) toggleFields.push(ToggleField.MARGIN);

  const numberOfContinuousStopsTillBotStop: string | MultiValue | undefined =
    safeNumberToString(bot.number_of_continuous_stops_till_bot_stop);
  const advancedSettings: AdvancedSettings<string | MultiValue | undefined> = {
    number_of_trades_till_bot_stop: safeNumberToString(
      bot.number_of_trades_till_bot_stop
    ),
    cooldown_between_opening_trades: safeNumberToString(
      bot.cooldown_between_opening_trades
    ),
    cooldown_after_trade_close: safeNumberToString(
      bot.cooldown_after_trade_close
    ),
    number_of_simultaneous_trades: safeNumberToString(
      bot.number_of_simultaneous_trades
    ),
    cooldown_between_opening_trades_per_symbol: safeNumberToString(
      bot.cooldown_between_opening_trades_per_symbol
    ),
    min_symbol_price: safeNumberToString(bot.min_symbol_price),
    min_symbol_volume: safeNumberToString(bot.min_symbol_volume),
    concurrent_max_usd_invested: safeNumberToString(
      bot.concurrent_max_usd_invested
    ),
    number_of_simultaneous_trades_per_symbol: safeNumberToString(
      bot.number_of_simultaneous_trades_per_symbol
    ),
    time_stop_seconds: safeNumberToString(bot.time_stop_seconds),
    auto_cancel_timeout_seconds: safeNumberToString(
      bot.auto_cancel_timeout_seconds
    ),
  };

  const {
    amountType,
    amountValue,
    amountPercentageBaseType,
    amountCustomBaseValue,
  } = getBotAmountTypeAndValue(bot, hasAmountPercentageFeatureFlag);
  if (
    (amountType && amountValue) ||
    (!isChannelConfiguration && !isTradingViewSignalsBot)
  )
    toggleFields.push(ToggleField.AMOUNT_PER_TRADE);

  const skipWithoutStopLoss = bot.skip_without_stoploss;
  if (skipWithoutStopLoss) {
    toggleFields.push(ToggleField.SKIP_WITHOUT_STOP_LOSS);
  }

  const cancelOnProfitBeforeEntry = getToggleFieldValue(
    bot.cancel_on_profit_before_entry === null
      ? channelValue
      : bot.cancel_on_profit_before_entry,
    ToggleField.CLOSE_ON_TP_BEFORE_ENTRY
  );
  if (cancelOnProfitBeforeEntry)
    toggleFields.push(ToggleField.CLOSE_ON_TP_BEFORE_ENTRY);

  const getAccount = () => {
    if (!getAccountById || !bot.client_id) return;
    return getAccountById?.(bot.client_id);
  };

  const getSymbols = () => {
    if (getExchangeSymbols || getAccountActiveSymbols) {
      const exchangeSymbols =
        getAccountActiveSymbols?.(bot.client_id) ??
        getExchangeSymbols?.(bot.exchange);
      if (isSignalsBot) {
        if (bot.blacklist_symbols?.length) {
          const blacklistSet = new Set(bot.blacklist_symbols);
          return exchangeSymbols?.filter((symbol) => !blacklistSet.has(symbol));
        } else if (!bot.symbols) return exchangeSymbols;
      }
      const exchangeSymbolsSet = new Set(exchangeSymbols);
      return bot.symbols?.filter((symbol) => exchangeSymbolsSet.has(symbol));
    }
    return bot.symbols;
  };

  const account = getAccount();
  const isRangeHedgingBot =
    isHedgingBot &&
    hasValue(bot.max_grid_price) &&
    hasValue(bot.lower_price_long_exposure_ratio) &&
    hasValue(bot.upper_price_long_exposure_ratio) &&
    hasValue(entryStrategy);

  const botData: BaseBotFormValues = {
    account,
    accounts: account ? [account] : [],
    exchanges: sortByExchangeOrder(bot.exchanges),
    subGroup: undefined,
    name: "name" in bot ? bot.name : "",
    symbols: getSymbols() || [],
    useSymbolFromAlert:
      !bot.symbols && (isTradingViewBot || isTradingViewSignalsBot),
    includeNewSymbols: isSignalsBot && !bot.symbols,
    amountType,
    amountValue,
    amountPercentageBaseType: hasAmountPercentageFeatureFlag
      ? amountPercentageBaseType ?? undefined
      : undefined,
    amountCustomBaseValue: hasAmountPercentageFeatureFlag
      ? amountCustomBaseValue
      : undefined,
    longShort: bot.directions,
    marginType: bot.margin_type ?? undefined,
    multiplier: bot.leverage ?? undefined,
    cancelOnProfitBeforeEntry,
    stopType: getBotStopType(bot),
    stopLimitPriceReduction: getStopLimitPriceReduction(bot),
    trailingStopData,
    entryStrategy,
    takeProfitStrategy,
    defaultTakeProfit,
    defaultStop,
    trailingEntry,
    trailingTakeProfit,
    trailingTakeProfitLastTargetOnly:
      !!bot.trailing_take_profit_last_target_only,
    stopTimeout,
    numberOfContinuousStopsTillBotStop,
    advancedSettings,
    entryGracePercent,
    entryGraceBaseline: bot.entry_grace_baseline,
    multiSymbolMarginData,
    gridsData: isGridBot
      ? getBotGridsData(
          bot.max_grid_price,
          entryStrategy,
          !!bot.reverse,
          pricePrecision
        )
      : undefined,
    hedgingMode: isRangeHedgingBot
      ? HedgingMode.RANGE
      : isHedgingBot
      ? HedgingMode.FIXED
      : undefined,
    hedgingGridsData: isRangeHedgingBot
      ? {
          ...getBotGridsData(
            bot.max_grid_price,
            entryStrategy,
            false,
            pricePrecision
          ),
          lowerPriceLongExposureRatio: toFixed(
            (bot.lower_price_long_exposure_ratio || 0) * 100,
            2
          ).toString(),
          upperPriceLongExposureRatio: toFixed(
            (bot.upper_price_long_exposure_ratio || 0) * 100,
            2
          ).toString(),
        }
      : undefined,
    skipWithoutStopLoss,
    followFuturesAsSpot:
      "follow_futures_as_spot" in bot ? !!bot.follow_futures_as_spot : false,
    takeProfitGracePercent,
    entryGraceTargetExchange: bot.entry_grace_target_exchange ?? undefined,
    takeProfitGraceTargetExchange:
      bot.take_profit_grace_target_exchange ?? undefined,
    operationHoursEvents,
    keywordToSymbolMapping: bot.keyword_to_symbol_mapping ?? undefined,
    longHedgingRatio:
      typeof bot.long_exposure_ratio === "number"
        ? toFixed(bot.long_exposure_ratio * 100, 2)
        : 50,
  };

  const parsedToggleFields =
    isHedgingBot && !isRangeHedgingBot
      ? toggleFields.filter(
          (field) =>
            field !== ToggleField.ENTRIES &&
            !ENTRY_STRATEGY_TOGGLE_FIELDS.has(field)
        )
      : toggleFields;

  return [botData, new Set(parsedToggleFields), new Set(overrideFields)];
};

export const getDefaultStopType = (
  options?: NumberOption[],
  defaultStopType?: StopType
) => {
  if (!options?.length) return;
  if (options.length > 1) {
    if (
      defaultStopType &&
      options.find((option) => option.value === defaultStopType)
    ) {
      return defaultStopType;
    }
    return StopType.MARKET;
  } else if (options.length === 1) {
    return options[0]?.value;
  }
};

export const getBotStopType = (
  bot: BotData | BaseBotData,
  hideMissingFields?: boolean
) => {
  if (hideMissingFields && !("stop_type" in bot)) return;
  const stopTypeOptions = bot.options?.["Stop Type"];
  return (
    bot.stop_type ??
    getDefaultStopType(stopTypeOptions?.[0], stopTypeOptions?.[1])
  );
};

export const hasMultiValues = (object: any): boolean => {
  if (isSymbol(object)) return true;
  else if (isObject(object)) {
    return Object.values(object).some((value) => hasMultiValues(value));
  } else if (Array.isArray(object)) {
    return object.some((value) => hasMultiValues(value));
  }
  return false;
};

export const getMinUsdAmountPerTrade = (exchange?: ExchangeName) => {
  if (exchange && BINANCE_EXCHANGES.has(exchange))
    return MIN_BINANCE_USD_AMOUNT_PER_TRADE;
  return MIN_USD_AMOUNT_PER_TRADE;
};

export const setBotFormValues = (
  formSetters: BotFormSetters,
  baseFormValues?: BaseBotFormValues,
  keysToSet?: (keyof BaseBotFormValues)[],
  keysToIgnore?: (keyof BaseBotFormValues)[]
) => {
  if (!baseFormValues) return;
  Object.entries(baseFormValues).forEach(([botKey, botValue]) => {
    if (
      (!keysToSet || keysToSet.includes(botKey as keyof BaseBotFormValues)) &&
      (!keysToIgnore ||
        !keysToIgnore.includes(botKey as keyof BaseBotFormValues))
    ) {
      const setterName =
        BOT_FORM_KEY_TO_SETTER_NAME[botKey as keyof BaseBotFormValues];
      const setter = formSetters[setterName];
      // @ts-ignore
      setter?.(botValue);
    }
  });
};

export const validateGridsData = (
  gridsData: BaseBotFormValues["gridsData"]
) => {
  const priceRange = gridsData?.priceRange;
  const isInvalidPrices =
    priceRange?.min === undefined || priceRange?.max === undefined;
  const isPriceError =
    !priceRange || priceRange.min === undefined || priceRange.max === undefined;
  let priceErrorMessage = isPriceError
    ? "Please provide both a minimum and a maximum price for the grids."
    : undefined;
  if (
    !isInvalidPrices &&
    priceRange.min &&
    priceRange.max &&
    !isSymbol(priceRange.min) &&
    !isSymbol(priceRange.max) &&
    parseFloat(priceRange.min) >= parseFloat(priceRange.max)
  ) {
    priceErrorMessage =
      "The minimum price must be lower than the maximum price";
  }
  let numGridsError = gridsData?.numGrids
    ? undefined
    : "Please select the number of grids";
  if (gridsData?.numGrids && !isSymbol(gridsData.numGrids)) {
    const numGrids = parseInt(gridsData?.numGrids);
    if (numGrids < MIN_NUM_GRIDS || numGrids > MAX_NUM_GRIDS)
      numGridsError = `The number of grids should be between ${MIN_NUM_GRIDS} and ${MAX_NUM_GRIDS}`;
  }

  return {
    priceErrorMessage,
    numGridsError,
    isValid: !priceErrorMessage && !numGridsError,
  };
};

export const getAdvancedKeys = (
  botType: BotType,
  includeTimeStop = true
): (keyof AdvancedSettings)[] =>
  getAllAdvancedKeys(botType).filter(
    (key) => includeTimeStop || key !== "time_stop_seconds"
  );

const getAllAdvancedKeys = (botType: BotType): (keyof AdvancedSettings)[] => {
  if ([BotType.GRID, BotType.HEDGING].includes(botType)) return [];
  if (botType === BotType.ASAP)
    return [
      "cooldown_after_trade_close",
      "number_of_trades_till_bot_stop",
      "time_stop_seconds",
    ];
  if (botType === BotType.SIGNAL) {
    return [
      "number_of_simultaneous_trades",
      "number_of_simultaneous_trades_per_symbol",
      "cooldown_between_opening_trades_per_symbol",
      "min_symbol_price",
      "min_symbol_volume",
      "concurrent_max_usd_invested",
      "auto_cancel_timeout_seconds",
      "time_stop_seconds",
    ];
  }
  if (botType === BotType.TRADING_VIEW_SIGNALS) {
    return ["number_of_simultaneous_trades", "time_stop_seconds"];
  }
  return [
    "number_of_simultaneous_trades",
    "number_of_trades_till_bot_stop",
    "time_stop_seconds",
    "cooldown_between_opening_trades",
    "cooldown_after_trade_close",
  ];
};

export const getIsReverse = (longShort: BaseBotFormValues["longShort"]) =>
  !!longShort && !isSymbol(longShort) && longShort?.includes(LongShort.SHORT);

export const getGridsPriceDifference = (
  gridsData: BaseBotFormValues["gridsData"],
  reverse?: boolean
) => {
  const { min, max } = gridsData?.priceRange || {};
  if (
    !min ||
    !max ||
    !gridsData?.numGrids ||
    isSymbol(min) ||
    isSymbol(max) ||
    isSymbol(gridsData.numGrids)
  )
    return;
  const minPrice = parseFloat(min);
  const maxPrice = parseFloat(max);
  const numGrids = parseInt(gridsData?.numGrids);
  let priceDifference = 0;
  if (gridsData?.gridType === GridType.GEOMETRIC) {
    priceDifference = reverse
      ? (maxPrice / minPrice) ** (1 / (numGrids - 1)) - 1
      : 1 - (minPrice / maxPrice) ** (1 / (numGrids - 1));
  } else if (gridsData?.gridType === GridType.ARITHMETIC) {
    priceDifference = reverse
      ? -(minPrice - maxPrice) / (numGrids - 1) / minPrice
      : (maxPrice - minPrice) / (numGrids - 1) / maxPrice;
  }
  return priceDifference * 100;
};

const getPricesRange = (prices: number[]) => {
  prices.sort(sortByNumberProperty);
  return {
    min: prices[0]?.toString(),
    max: prices[prices.length - 1]?.toString(),
  };
};

export const getRawStrategyPriceRange = (
  strategy: StrategyData,
  basePrice: number | undefined,
  isReverse: boolean,
  pricePrecision?: number
) => {
  if (strategy.strategy_style !== StrategyStyleType.DCA || !basePrice) return;
  const priceScale = strategy?.price_scale;
  const prices = getOrdersPrices(
    strategy.number_of_orders,
    strategy.price_difference,
    priceScale,
    strategy.baseline,
    basePrice,
    pricePrecision || 0,
    OrderTypes.ENTRY,
    isReverse
  );
  return getPricesRange(prices);
};

export const getBotFormStrategyPriceRange = (
  strategy: BaseBotFormValues["entryStrategy"],
  basePrice: number | undefined,
  isReverse: boolean,
  pricePrecision?: number
) => {
  if (isSymbol(strategy) || !basePrice || !strategy?.price_difference)
    return {
      min: undefined,
      max: undefined,
    };
  const numGrids = getStrategyNumOrders(strategy);
  const priceScale = strategy?.price_scale;
  const prices = getOrdersPrices(
    numGrids,
    strategy?.price_difference,
    priceScale,
    strategy?.baseline,
    basePrice,
    pricePrecision || 0,
    OrderTypes.ENTRY,
    isReverse
  );
  return getPricesRange(prices);
};

export const getStrategyFromGridsData = (
  currentStrategy: BaseBotFormValues["entryStrategy"],
  gridsData: BaseBotFormValues["gridsData"],
  reverse?: boolean,
  updateBaseline = true
) => {
  const numberOfOrders =
    typeof gridsData?.numGrids === "string"
      ? parseInt(gridsData?.numGrids)
      : gridsData?.numGrids;
  let strategy: BaseBotFormValues["entryStrategy"] = {
    ...(isSymbol(currentStrategy) ? undefined : currentStrategy),
    strategy_style: StrategyStyleType.DCA,
    number_of_orders: numberOfOrders,
  };
  if (gridsData?.gridType !== GridType.CUSTOM) {
    strategy.price_scale = 1;
    strategy.price_difference = getGridsPriceDifference(gridsData, reverse);
  }
  if (updateBaseline) {
    strategy.baseline =
      gridsData?.gridType === GridType.ARITHMETIC
        ? StrategyBaseline.FIRST_ORDER
        : StrategyBaseline.PREVIOUS_ORDER;
  }
  return strategy;
};

export const getUpdatedStrategyAndGridsData = (
  gridsData: GridsData,
  entryStrategy: BaseBotFormValues["entryStrategy"],
  isReverse: boolean,
  pricePrecision?: number,
  updateBaseline?: boolean
) => {
  const updatedGridsData = { ...gridsData };
  const updatedStrategy = getStrategyFromGridsData(
    entryStrategy,
    updatedGridsData,
    isReverse,
    updateBaseline
  );
  if (updatedGridsData.gridType === GridType.CUSTOM) {
    updatedGridsData.priceRange = getBotFormStrategyPriceRange(
      updatedStrategy,
      getGridsParsedBasePrice(updatedGridsData, isReverse),
      isReverse,
      pricePrecision
    );
  }

  return {
    updatedStrategy,
    updatedGridsData,
  };
};

export const getGridsParsedBasePrice = (
  gridsData: BaseBotFormValues["gridsData"],
  reverse?: boolean
) => {
  const priceRange = gridsData?.priceRange;
  const basePrice = reverse ? priceRange?.min : priceRange?.max;
  return basePrice && !isSymbol(basePrice) ? parseFloat(basePrice) : undefined;
};

export const splitOrdersByCurrentPrice = (
  orders: StrategyOrder[] | undefined,
  currentPrice?: number,
  reverse?: boolean
) => {
  if (!orders || !currentPrice) return [[], []];
  const [entryOrders, takeProfitOrders] = partition(orders, (entry) =>
    reverse ? entry.price > currentPrice : entry.price < currentPrice
  );
  return [entryOrders, takeProfitOrders];
};

export const splitGridBotEntries = (
  orders: StrategyOrder[] | undefined,
  currentPrice?: number,
  reverse?: boolean
) => {
  if (!orders || !orders.length) return [[], []];
  let firstTakeProfits = orders[0];
  let lastEntry = orders[orders.length - 1];
  const [restEntries, restTakeProfits] = splitOrdersByCurrentPrice(
    orders.slice(1, orders.length - 1),
    currentPrice,
    reverse
  );

  return [
    [...restEntries, lastEntry],
    [firstTakeProfits, ...restTakeProfits],
  ];
};

export const getMaxPriceDifferenceHelperData = (
  botType?: BotType,
  t?: (key: TranslationKeys<LanguageNameSpaces.BOTS>, options?: any) => string
) => {
  const isGridBot = botType === BotType.GRID;
  const orderName = isGridBot ? "grid" : "order";
  return {
    analyticsFieldName: t?.(
      isGridBot ? GRID_MAX_PRICE_DIFFERENCE_TEXT : MAX_PRICE_DIFFERENCE_TEXT
    ),
    analyticsPageName: AnalyticsPageName.BOT_FORM,
    description: t?.(
      isGridBot
        ? "helper_tooltips.max_price_difference.grid_price_range_description"
        : "helper_tooltips.max_price_difference.description",
      { orderName }
    ),
  };
};

export const getGridsDataFromStrategy = (
  strategy: BaseBotFormValues["entryStrategy"],
  isReverse: boolean,
  basePrice?: number,
  pricePrecision?: number
): GridsData | undefined => {
  if (isSymbol(strategy) || !basePrice || !strategy?.price_difference) return;
  const numGrids = getStrategyNumOrders(strategy);
  const priceRange = getBotFormStrategyPriceRange(
    strategy,
    basePrice,
    isReverse,
    pricePrecision
  );
  let gridType = GridType.GEOMETRIC;
  if (strategy.price_scale !== 1) gridType = GridType.CUSTOM;
  else if (strategy?.baseline === StrategyBaseline.FIRST_ORDER)
    gridType = GridType.ARITHMETIC;
  return {
    numGrids: numGrids?.toString(),
    priceRange: priceRange,
    gridType,
  };
};

export const getBotFormDefaultTakeProfitValue = (
  botType: BotType,
  isChannelConfiguration?: boolean
) => {
  if (botType === BotType.SIGNAL && !isChannelConfiguration)
    return DEFAULT_SIGNAL_BOT_TAKE_PROFIT;
  if (botType === BotType.TRADING_VIEW_SIGNALS)
    return DEFAULT_FIRST_ENTRY_TAKE_PROFIT;
  return DEFAULT_TAKE_PROFIT;
};

export const getBotFormDefaultStopValue = (botType: BotType) => {
  if (botType === BotType.SIGNAL) return channelValue;
  if (botType === BotType.GRID) return DEFAULT_GRID_BOT_STOP;
  return DEFAULT_STOP;
};

export const getBotInitialAmount = (
  parsedValidationEntries: StrategyOrder[] | undefined,
  mainCoinAmountPrecision: number,
  botType?: BotType
): InitialBotAmountData => {
  const immediateOrders = parsedValidationEntries?.filter(
    (order) => order.isImmediate
  );
  const activeOrders = parsedValidationEntries?.filter(
    (order) => order.isActive
  );
  const validationActiveOrders =
    botType === BotType.GRID ? parsedValidationEntries : activeOrders;
  return {
    initialUsdAmount: toFixed(
      immediateOrders?.reduce((acc, order) => acc + order.usdAmount, 0) || 0,
      2
    ),
    initialMainCoinAmount: toFixed(
      immediateOrders?.reduce((acc, order) => acc + order.amount, 0) || 0,
      mainCoinAmountPrecision
    ),
    activeUsdAmount: toFixed(
      activeOrders?.reduce((acc, order) => acc + order.usdAmount, 0) || 0,
      2
    ),
    activeMainCoinAmount: toFixed(
      activeOrders?.reduce((acc, order) => acc + order.amount, 0) || 0,
      mainCoinAmountPrecision
    ),
    validationActiveUsdAmount: toFixed(
      validationActiveOrders?.reduce(
        (acc, order) => acc + order.usdAmount,
        0
      ) || 0,
      2
    ),
    validationActiveMainCoinAmount: toFixed(
      validationActiveOrders?.reduce((acc, order) => acc + order.amount, 0) ||
        0,
      mainCoinAmountPrecision
    ),
  };
};

export const getMinEntryOrderAmountError = (isMarginExchange?: boolean) => {
  return `One or more of the entry orders amounts are lower than the minimum that is allowed in the exchange. Please increase the amount per trade${
    isMarginExchange ? ", adjust the leverage, " : ""
  } or change the Entries strategy.`;
};

export const getActiveEntriesAmountError = (
  botType: BotType,
  isStrategyError?: boolean,
  hasMinEntryOrderAmountError?: boolean
) => {
  let suffix = "";
  if (isStrategyError) {
    suffix = `. Please ${
      hasMinEntryOrderAmountError ? "" : "decrease the amount per trade or "
    }change the Entries strategy.`;
  }
  if (botType === BotType.GRID) {
    return `The amount in the grid orders is higher than your available amount${suffix}`;
  }
  return `The amount in the initial market order and active entries is higher than your available amount${suffix}`;
};

export const getBotGridsData = (
  maxGridPrice: number | undefined,
  entryStrategy: BaseBotFormValues["entryStrategy"],
  isReverse: boolean,
  pricePrecision: number | undefined
) => {
  if (isSymbol(entryStrategy)) return;
  let gridType: GridType = GridType.ARITHMETIC;
  if (entryStrategy?.price_scale !== 1) gridType = GridType.CUSTOM;
  else if (entryStrategy?.baseline === StrategyBaseline.PREVIOUS_ORDER)
    gridType = GridType.GEOMETRIC;
  return {
    priceRange: getBotFormStrategyPriceRange(
      entryStrategy,
      maxGridPrice,
      isReverse,
      pricePrecision
    ),
    gridType,
    numGrids: getStrategyNumOrders(entryStrategy)?.toString(),
  };
};

export const showInvalidOverlapValueMessage = (
  overlapKey: keyof OverlapData,
  openMessage?: OpenMessage,
  t?: (
    key: TranslationKeys<LanguageNameSpaces.BOTS>,
    options?: Record<string, unknown>
  ) => string
) => {
  const message = t?.("messages.warnings.value_not_valid_in_exchanges", {
    field: t(OVERLAP_KEY_TO_DISPLAY[overlapKey]),
  });
  if (message) openMessage?.(message, "warning");
};

export const checkValidOverlapValue = (
  overlapData: OverlapData | undefined,
  overlapKey: keyof OverlapData,
  value: number | string,
  openMessage?: OpenMessage,
  t?: (key: TranslationKeys<LanguageNameSpaces.BOTS>) => string
) => {
  const specificOverlapData = overlapData?.[overlapKey];
  if (
    !specificOverlapData ||
    // @ts-ignore
    specificOverlapData.overlappingValues?.includes(value)
  ) {
    return true;
  } else {
    showInvalidOverlapValueMessage(overlapKey, openMessage, t);
  }
};

export const getParentFieldChecked = (
  botType: BotType,
  formValues: BotFormValues,
  checkedFields: Set<ToggleField>,
  toggleFieldType?: ToggleField,
  ignoreSymbolParent?: boolean
) => {
  const isSignalBot = botType === BotType.SIGNAL;
  let fieldToParentMapping = FIELD_TO_PARENT_FIELD;
  if (isSignalBot) {
    fieldToParentMapping = {};
  }

  const parentToggleField = toggleFieldType
    ? fieldToParentMapping[toggleFieldType]
    : undefined;
  const parentFormKey =
    parentToggleField && TOGGLE_FIELD_TO_FORM_KEY[parentToggleField];
  const parentValue = parentFormKey && formValues[parentFormKey];
  const parentIsMultiValue = isSymbol(parentValue);
  return (
    (!ignoreSymbolParent || !parentIsMultiValue) &&
    (!parentToggleField || checkedFields.has(parentToggleField))
  );
};

export const getStrategyTab = (
  strategy: BaseBotFormValues["entryStrategy"],
  botType: BotType
) => {
  if (strategy && !isSymbol(strategy) && !isSymbol(strategy.strategy_style)) {
    return STRATEGY_TYPE_MAPPING[strategy.strategy_style];
  }
  return botType === BotType.SIGNAL ? StrategyTabs.BUILT_IN : StrategyTabs.DCA;
};

export const getIsStrategyAdvanced = (
  strategy: BaseBotFormValues["entryStrategy"],
  botType: BotType,
  isChannelConfiguration?: boolean
) => {
  if (botType === BotType.SIGNAL) {
    if (isChannelConfiguration) return !!strategy;
    return (
      strategy &&
      !isSymbol(strategy) &&
      Object.values(strategy).some(
        (strategyValue) => !!strategyValue && !isSymbol(strategyValue)
      )
    );
  } else if (!strategy) return false;
  else if (botType === BotType.GRID) {
    if (isSymbol(strategy)) return true;
    return (
      [
        "strategy_style",
        "amount_scale",
        "price_scale",
        "number_of_active_orders",
        "baseline",
      ] as (keyof StrategyData)[]
    ).some((key) => {
      return strategy[key] !== DEFAULT_GRID_BOT_STRATEGY[key];
    });
  }
};

export const getIsDefaultOrderAdvanced = (
  defaultOrder: BaseBotFormValues["defaultStop"],
  checkedFields: Set<ToggleField>,
  orderType: OrderTypes,
  botType: BotType
) => {
  if (botType === BotType.SIGNAL) {
    return (
      !isSymbol(defaultOrder) &&
      (defaultOrder?.baseline !== DEFAULT_SIGNAL_BOT_TAKE_PROFIT?.baseline ||
        defaultOrder?.percent !== DEFAULT_SIGNAL_BOT_TAKE_PROFIT?.percent)
    );
  }
  let toggleFields: ToggleField[] = [];
  if (orderType === OrderTypes.TAKE_PROFIT) {
    toggleFields = [ToggleField.TAKE_PROFITS];
  } else if (orderType === OrderTypes.STOP) {
    toggleFields = [ToggleField.STOP];
  }
  return (
    !isSymbol(defaultOrder) &&
    toggleFields.some((field) => checkedFields.has(field))
  );
};

export const getAreBotValuesAdvanced = (
  botValues: BaseBotFormValues,
  rawBotData: BaseBotData | BotData | undefined,
  checkedFields: Set<ToggleField>,
  overrideFields: Set<OverrideFieldType>,
  botType: BotType,
  isChannelConfiguration?: boolean
) => {
  const isSignalsBot = botType === BotType.SIGNAL;
  const strategies: BaseBotFormValues["entryStrategy"][] = [
    botValues.entryStrategy,
  ];
  if (isSignalsBot) strategies.push(botValues.takeProfitStrategy);
  const isAdvancedStrategies = strategies.some((strategy) =>
    getIsStrategyAdvanced(strategy, botType, isChannelConfiguration)
  );

  const advancedSettingsData = botValues.advancedSettings;
  const stopTypeOptions = rawBotData?.options?.["Stop Type"];
  const defaultStopType = getDefaultStopType(
    stopTypeOptions?.[0],
    stopTypeOptions?.[1]
  );
  const defaultStopLimitPriceReduction =
    defaultStopType === StopType.LIMIT
      ? DEFAULT_STOP_LIMIT_PRICE_REDUCTION
      : undefined;

  const isTrailingStopAdvanced =
    botValues.trailingStopData &&
    !isSymbol(botValues.trailingStopData) &&
    botValues.trailingStopData.type !== 0;

  const isLongShortAdvanced =
    rawBotData?.options?.["Long/Short"]?.[0] &&
    botValues.longShort &&
    !isSymbol(botValues.longShort) &&
    botValues.longShort.length < 2;

  const isOverridesAdvanced =
    isSignalsBot &&
    Object.values(OverrideFieldType).some((fieldType) => {
      return (
        overrideFields.has(fieldType) !== DEFAULT_OVERRIDE_FIELDS.has(fieldType)
      );
    });
  const isSymbolsAdvanced =
    !isSymbol(botValues.symbols) &&
    (!!rawBotData?.symbols?.length || !!rawBotData?.blacklist_symbols?.length);
  const isAmountAdvanced =
    botValues.amountPercentageBaseType === PercentageBaseType.CUSTOM_AMOUNT;
  const isOperationHoursAdvanced =
    rawBotData?.blacklisted_operation_hours?.length;

  return (
    isAmountAdvanced ||
    isAdvancedStrategies ||
    isSymbolsAdvanced ||
    ADVANCED_BOT_FORM_KEYS.some((formKey) => {
      const value = botValues[formKey];
      return !!value && value !== channelValue;
    }) ||
    botValues.keywordToSymbolMapping ||
    !!xor([...getDefaultCheckedFields(botType)], [...checkedFields]).length ||
    ADVANCED_SETTINGS_KEYS.some((formKey) => {
      const isNumTradesPerSymbol =
        formKey === "number_of_simultaneous_trades_per_symbol";
      const value = advancedSettingsData[formKey];
      if (isNumTradesPerSymbol && isSignalsBot) {
        return value !== DEFAULT_NUMBER_OF_SIMULTANEOUS_TRADES_PER_SYMBOL;
      }
      return !!value;
    }) ||
    getIsDefaultOrderAdvanced(
      botValues.defaultStop,
      checkedFields,
      OrderTypes.STOP,
      botType
    ) ||
    getIsDefaultOrderAdvanced(
      botValues.defaultTakeProfit,
      checkedFields,
      OrderTypes.TAKE_PROFIT,
      botType
    ) ||
    defaultStopType !== botValues.stopType ||
    defaultStopLimitPriceReduction?.toString() !==
      botValues.stopLimitPriceReduction ||
    isTrailingStopAdvanced ||
    isLongShortAdvanced ||
    isOverridesAdvanced ||
    isOperationHoursAdvanced
  );
};

export const getDefaultCheckedFields = (
  botType: BotType,
  isChannelConfiguration?: boolean
) => {
  let toggleFields: ToggleField[] = [];
  if (isChannelConfiguration) {
    return new Set(toggleFields);
  }
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  if (!isTradingViewSignalsBot) {
    toggleFields.push(ToggleField.AMOUNT_PER_TRADE);
  }
  const isSignalsBot = botType === BotType.SIGNAL;
  const isHedgingBot = botType === BotType.HEDGING;
  if (!isSignalsBot && !isHedgingBot)
    toggleFields = toggleFields.concat([
      ToggleField.ENTRY_PRICE_STRATEGY,
      ToggleField.ENTRY_RATIOS,
      ToggleField.ENTRY_NUM_ORDERS,
      ToggleField.ENTRY_NUM_ACTIVE_ORDERS,
      ToggleField.TAKE_PROFIT_PRICE_STRATEGY,
      ToggleField.TAKE_PROFIT_RATIOS,
      ToggleField.TAKE_PROFIT_NUM_ORDERS,
      ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS,
    ]);
  if (isHedgingBot)
    toggleFields = toggleFields.concat([
      ToggleField.ENTRIES,
      ToggleField.ENTRY_PRICE_STRATEGY,
      ToggleField.ENTRY_NUM_ORDERS,
    ]);
  if (botType === BotType.ASAP)
    toggleFields = toggleFields.concat([
      ToggleField.TAKE_PROFITS,
      ToggleField.ENTRIES,
    ]);
  else if (botType === BotType.GRID)
    toggleFields = toggleFields.concat([ToggleField.ENTRIES]);
  else if (isSignalsBot)
    toggleFields = toggleFields.concat([
      ...ALL_OVERRIDES_TOGGLE_FIELDS,
      ...WITHOUT_OFF_TOGGLE_FIELDS,
    ]);
  return new Set(toggleFields);
};

export const isCustomStrategy = (
  parsedStrategy: StrategyData | undefined
): parsedStrategy is CustomStrategyData => {
  return (
    !!parsedStrategy &&
    parsedStrategy.strategy_style === StrategyStyleType.CUSTOM
  );
};

export const isDcaStrategy = (
  parsedStrategy: StrategyData | undefined
): parsedStrategy is DCAStrategyData => {
  return (
    !!parsedStrategy && parsedStrategy.strategy_style === StrategyStyleType.DCA
  );
};

export const isBuiltInStrategy = (
  parsedStrategy: StrategyData | undefined
): parsedStrategy is BuiltInStrategyData => {
  return (
    !!parsedStrategy &&
    parsedStrategy.strategy_style === StrategyStyleType.BUILT_IN
  );
};
